/**
 * @jaks-crm/shared
 *
 * Pure constants and helpers shared by api/ and web/. No runtime dependencies:
 * everything here must run in a Lambda, a browser, and a vitest worker.
 */

// ---------------------------------------------------------------------------
// Pipeline stages
// ---------------------------------------------------------------------------

/**
 * Pipeline stages, in board order.
 *
 * The middle of this list is not the CRM's invention: it is JobTread's own "Job Status"
 * custom field (`22PCHVmPaAB3`), option for option, in JobTread's own order. James, 2026-08-21:
 * "the pipeline is not reflective of the actual jobtread estimating pipeline view". It now is.
 *
 *   intake        before a JobTread job exists at all (CRM-only, no Job Status to read)
 *   jobtread      the estimating pipeline, mirrored from Job Status
 *   construction  after the sale, driven by the Construction Status field
 *   exits         declined (the JobTread option) and disqualified (never belonged here)
 *
 * `pending_decision` is one stage carrying `decision_day` 3 / 7 / 21, because JobTread models
 * the same three states as three options of one field. The board renders one column per day.
 */
export const DEAL_STAGES = [
  // intake: only reachable before a JobTread job exists
  "new_lead",
  "contacted",
  "qualified",
  "site_visit",
  // JobTread Job Status, verbatim and in JobTread's order
  "more_info_needed",
  "estimating",
  "estimate_ready",
  "estimate_sent",
  "pending_decision",
  "approved",
  // post-sale, driven by Construction Status (plus the CRM-only tail)
  "pre_construction",
  "in_progress",
  "post_construction",
  "complete",
  "warranty",
  "closed",
  // exits
  "declined",
  "disqualified",
] as const;
export type DealStage = (typeof DEAL_STAGES)[number];

/**
 * Stage names written by earlier versions of the CRM. They are accepted on read so an
 * un-migrated row never crashes a screen; `approved` and `declined` supersede them and
 * nothing writes them any more.
 */
export const LEGACY_STAGE_ALIASES: Record<string, DealStage> = { won: "approved", lost: "declined" };

/** Canonical stage for any stored value, or null when it is not a stage at all. */
export function normalizeStage(s: string | null | undefined): DealStage | null {
  if (!s) return null;
  if ((DEAL_STAGES as readonly string[]).includes(s)) return s as DealStage;
  return LEGACY_STAGE_ALIASES[s] ?? null;
}

/** Stages a deal can hold before it has a JobTread job. New deals start here. */
export const INTAKE_STAGES: readonly DealStage[] = ["new_lead", "contacted", "qualified", "site_visit"];
/** The JobTread estimating pipeline, one stage per Job Status option (Declined is an exit). */
export const JT_PIPELINE_STAGES: readonly DealStage[] = ["more_info_needed", "estimating", "estimate_ready", "estimate_sent", "pending_decision", "approved"];
/** Post-sale stages. Only a sold job reaches these, and Construction Status drives them. */
export const CONSTRUCTION_STAGES: readonly DealStage[] = ["pre_construction", "in_progress", "post_construction", "complete", "warranty", "closed"];

/** Transition into this stage writes the JobTread outbox chain (account, contacts, location, job). */
export const PUSH_TRIGGER_STAGE: DealStage = "estimating";

/** Side exits; a deal can leave the funnel into either from any stage before the sale. */
export const EXIT_STAGES: readonly DealStage[] = ["declined", "disqualified"];

export function stageIndex(stage: DealStage | string): number {
  const c = normalizeStage(stage as string);
  return c ? DEAL_STAGES.indexOf(c) : -1;
}

/** True for every stage before the sale (the side exits count as pre-sale too). */
export function isPreWon(stage: DealStage | string): boolean {
  const c = normalizeStage(stage as string);
  if (!c) return false;
  if (EXIT_STAGES.includes(c)) return true;
  return stageIndex(c) < stageIndex("approved");
}

export function isValidStage(s: string): s is DealStage {
  return (DEAL_STAGES as readonly string[]).includes(s);
}

/** Accepts the legacy names too, for values read out of the database. */
export function isAcceptedStage(s: string): boolean {
  return normalizeStage(s) !== null;
}

// ---------------------------------------------------------------------------
// JobTread constants
// ---------------------------------------------------------------------------

export const JT_ORG_ID = "22NwkENqBJ5C";

/** JobTread custom field ids, verified 2026-08-21. Keyed by owner node then field. */
export const JT_CF = {
  customerAccount: {
    leadSource: "22NwkMpyDtrt",
    customerStatus: "22NwkMqG7eme",
    jobType: "22NwkMquGFPx",
    typeOfBuild: "22NwkMs5bNXt",
    appointment: "22NwkMsKyjFn",
    notes: "22NwkMsSXQTn",
    referredBy: "22NzUTDvcnG6",
    salesRep: "22P2wAfTf3ey",
    leadLostReason: "22PD4jxnGsVS",
  },
  customerContact: {
    email: "22NwkEPhGVPL",
    phone: "22NwkEPuFs8k",
    mobile: "22P2w7ZvBpL4",
    address: "22P2wCan7Tqg",
    notes: "22NwkMxBnVb4",
  },
  vendorAccount: {
    email: "22NwkEPhR63w",
    phone: "22NwkEPuNDPe",
    w9: "22NwkEPuT55P",
    coi: "22NwkMy8c7WC",
    coiExpires: "22NwkEPumM5R",
    licenseNumber: "22NwkMz7Gizy",
    type: "22NwkMyi8G6Y",
    paymentTerms: "22NwkMzKJbX4",
    specialities: "22NwkN2CpwEm",
    rating: "22NzUTJM3EPd",
    website: "22NwkMyXLzd6",
    notes: "22NwkMzzpHFc",
  },
  vendorContact: {
    email: "22NwkEPhN6rd",
    phone: "22NwkEPuHMhv",
  },
  location: {
    notes: "22NwkN4KDasW",
    entryCode: "22NwkN4SWdzd",
  },
  job: {
    jobStatus: "22PCHVmPaAB3",
    constructionStatus: "22PCmMpP8EGy",
    jobType: "22NwkN94eDBB",
    salesRep: "22NwkN9AV7qY",
    estimator: "22NwkN9UJc6Q",
    notes: "22NwkNAUs37C",
    customerPo: "22NwkNAz3fta",
  },
} as const;

/** Exact option text of the Job Status custom field (`22PCHVmPaAB3`). */
export const JT_JOB_STATUS = {
  moreInfo: "More Information Needed",
  estimating: "Estimating",
  estimateReady: "Estimate Ready",
  estimateSent: "Estimate Sent",
  pendingDay3: "Pending Decision Day 3",
  pendingDay7: "Pending Decision Day 7",
  pendingDay21: "Pending Decision Day 21",
  approved: "Approved",
  declined: "Declined",
} as const;

export const JT_CONSTRUCTION_STATUS = {
  preConstruction: "Pre-Construction",
  inProgress: "In Progress",
  postConstruction: "Post-Construction",
  complete: "Complete",
} as const;

export const JT_CUSTOMER_STATUS = {
  prospect: "Prospect",
  qualified: "Qualified",
  active: "Active",
  complete: "Complete",
} as const;

/**
 * CRM customer status -> JobTread "Customer Status" option.
 *
 * JobTread's option list has no Lost, so a lost account reads Prospect there. That is also
 * why the reverse direction does not exist: the field cannot represent what the CRM knows,
 * and JAKS never maintained it by hand. See `deriveCustomerStatus`.
 */
export const CUSTOMER_STATUS_TO_JT: Record<string, string> = {
  prospect: JT_CUSTOMER_STATUS.prospect,
  qualified: JT_CUSTOMER_STATUS.qualified,
  active: JT_CUSTOMER_STATUS.active,
  complete: JT_CUSTOMER_STATUS.complete,
  lost: JT_CUSTOMER_STATUS.prospect,
};

/**
 * The single source of truth in the JobTread -> CRM direction: Job Status option text to the
 * stage it means. Nothing else may decide the stage of an unsold job.
 */
export interface JtJobStatusMapping {
  stage: DealStage;
  /** Only for the three Pending Decision options. */
  decisionDay?: 3 | 7 | 21;
}

export const JT_JOB_STATUS_TO_STAGE: Record<string, JtJobStatusMapping> = {
  [JT_JOB_STATUS.moreInfo]: { stage: "more_info_needed" },
  [JT_JOB_STATUS.estimating]: { stage: "estimating" },
  [JT_JOB_STATUS.estimateReady]: { stage: "estimate_ready" },
  [JT_JOB_STATUS.estimateSent]: { stage: "estimate_sent" },
  [JT_JOB_STATUS.pendingDay3]: { stage: "pending_decision", decisionDay: 3 },
  [JT_JOB_STATUS.pendingDay7]: { stage: "pending_decision", decisionDay: 7 },
  [JT_JOB_STATUS.pendingDay21]: { stage: "pending_decision", decisionDay: 21 },
  [JT_JOB_STATUS.approved]: { stage: "approved" },
  [JT_JOB_STATUS.declined]: { stage: "declined" },
};

/** JobTread's own option order, used to refuse a backwards push. */
export const JT_JOB_STATUS_ORDER: readonly string[] = [
  JT_JOB_STATUS.moreInfo,
  JT_JOB_STATUS.estimating,
  JT_JOB_STATUS.estimateReady,
  JT_JOB_STATUS.estimateSent,
  JT_JOB_STATUS.pendingDay3,
  JT_JOB_STATUS.pendingDay7,
  JT_JOB_STATUS.pendingDay21,
  JT_JOB_STATUS.approved,
  JT_JOB_STATUS.declined,
];

/** Position of a Job Status option in JobTread's order; -1 when it is not an option. */
export function jtJobStatusRank(option: string | null | undefined): number {
  return JT_JOB_STATUS_ORDER.indexOf((option ?? "").trim());
}

/**
 * The inverse of JT_JOB_STATUS_TO_STAGE. `pending_decision` needs the day to pick an option,
 * so `jtJobStatusForStage` handles that case; the intake stages and `disqualified` have no
 * Job Status at all and are absent.
 */
export const STAGE_TO_JT_JOB_STATUS: Partial<Record<DealStage, string>> = {
  more_info_needed: JT_JOB_STATUS.moreInfo,
  estimating: JT_JOB_STATUS.estimating,
  estimate_ready: JT_JOB_STATUS.estimateReady,
  estimate_sent: JT_JOB_STATUS.estimateSent,
  pending_decision: JT_JOB_STATUS.pendingDay3,
  approved: JT_JOB_STATUS.approved,
  declined: JT_JOB_STATUS.declined,
  // Construction stages all read Approved as far as the Job Status field is concerned.
  pre_construction: JT_JOB_STATUS.approved,
  in_progress: JT_JOB_STATUS.approved,
  post_construction: JT_JOB_STATUS.approved,
  complete: JT_JOB_STATUS.approved,
  warranty: JT_JOB_STATUS.approved,
  closed: JT_JOB_STATUS.approved,
};

/** Pending Decision Day 3 / 7 / 21 by whole days since the estimate was sent. */
export function pendingDecisionStatus(daysSinceSent: number): string {
  if (daysSinceSent >= 21) return JT_JOB_STATUS.pendingDay21;
  if (daysSinceSent >= 7) return JT_JOB_STATUS.pendingDay7;
  return JT_JOB_STATUS.pendingDay3;
}

/** Job Status option for a stage; `pending_decision` picks the option for its decision day. */
export function jtJobStatusForStage(stage: DealStage, decisionDay?: number | null): string | undefined {
  if (stage === "pending_decision") return pendingDecisionStatus(decisionDay ?? 3);
  return STAGE_TO_JT_JOB_STATUS[stage];
}

export interface StageJobTreadMapping {
  /** Job Status custom field option text. Absent when the stage has no job. */
  jobStatus?: string;
  /** Construction Status custom field option text. */
  constructionStatus?: string;
  /** Customer Status custom field option text on the customer account. */
  customerStatus?: string;
  /** Who drives this stage: CRM pushes or JobTread pulls. */
  direction: "crm" | "jobtread";
}

/**
 * What each stage implies in JobTread. Job Status is taken from STAGE_TO_JT_JOB_STATUS so the
 * two tables can never disagree; this one adds the Construction Status and Customer Status the
 * stage implies, and who drives it.
 */
export const STAGE_TO_JOBTREAD: Record<DealStage, StageJobTreadMapping> = {
  new_lead: { customerStatus: JT_CUSTOMER_STATUS.prospect, direction: "crm" },
  contacted: { customerStatus: JT_CUSTOMER_STATUS.prospect, direction: "crm" },
  qualified: { customerStatus: JT_CUSTOMER_STATUS.qualified, direction: "crm" },
  site_visit: { customerStatus: JT_CUSTOMER_STATUS.qualified, direction: "crm" },
  more_info_needed: {
    jobStatus: JT_JOB_STATUS.moreInfo,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.qualified,
    direction: "jobtread",
  },
  estimating: {
    jobStatus: JT_JOB_STATUS.estimating,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.qualified,
    direction: "jobtread",
  },
  estimate_ready: {
    jobStatus: JT_JOB_STATUS.estimateReady,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.qualified,
    direction: "jobtread",
  },
  estimate_sent: {
    jobStatus: JT_JOB_STATUS.estimateSent,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.qualified,
    direction: "jobtread",
  },
  pending_decision: {
    jobStatus: JT_JOB_STATUS.pendingDay3,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.qualified,
    direction: "jobtread",
  },
  approved: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.active,
    direction: "jobtread",
  },
  pre_construction: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.preConstruction,
    customerStatus: JT_CUSTOMER_STATUS.active,
    direction: "jobtread",
  },
  in_progress: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.inProgress,
    customerStatus: JT_CUSTOMER_STATUS.active,
    direction: "jobtread",
  },
  post_construction: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.postConstruction,
    customerStatus: JT_CUSTOMER_STATUS.active,
    direction: "jobtread",
  },
  complete: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.complete,
    customerStatus: JT_CUSTOMER_STATUS.complete,
    direction: "jobtread",
  },
  warranty: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.complete,
    customerStatus: JT_CUSTOMER_STATUS.complete,
    direction: "crm",
  },
  closed: {
    jobStatus: JT_JOB_STATUS.approved,
    constructionStatus: JT_CONSTRUCTION_STATUS.complete,
    customerStatus: JT_CUSTOMER_STATUS.complete,
    direction: "crm",
  },
  declined: { jobStatus: JT_JOB_STATUS.declined, customerStatus: JT_CUSTOMER_STATUS.prospect, direction: "jobtread" },
  disqualified: { customerStatus: JT_CUSTOMER_STATUS.prospect, direction: "crm" },
};

/**
 * Map a JobTread Construction Status option back to the deal stage it drives. This may only be
 * consulted for a SOLD job. A job still out for a decision is placed by its Job Status alone,
 * whatever Construction Status happens to say: JAKS stamps Pre-Construction on every job at
 * create time and edits it freely during the estimate, so before the sale the field carries no
 * information about the sale. Letting it through is exactly what hid B44 Breakroom (#10723).
 */
export const CONSTRUCTION_STATUS_TO_STAGE: Record<string, DealStage> = {
  [JT_CONSTRUCTION_STATUS.preConstruction]: "pre_construction",
  [JT_CONSTRUCTION_STATUS.inProgress]: "in_progress",
  [JT_CONSTRUCTION_STATUS.postConstruction]: "post_construction",
  [JT_CONSTRUCTION_STATUS.complete]: "complete",
};

/**
 * The one place a stage becomes words, for every surface: the board, the Account 360 deals
 * card, the reports, the tasks, the sync log. The JobTread stages read exactly as JobTread
 * writes them, so a person comparing the two screens sees the same words on both.
 */
export const STAGE_LABELS: Record<DealStage, string> = {
  new_lead: "New lead",
  contacted: "Contacted",
  qualified: "Qualified",
  site_visit: "Site visit",
  more_info_needed: JT_JOB_STATUS.moreInfo,
  estimating: JT_JOB_STATUS.estimating,
  estimate_ready: JT_JOB_STATUS.estimateReady,
  estimate_sent: JT_JOB_STATUS.estimateSent,
  pending_decision: "Pending Decision",
  approved: JT_JOB_STATUS.approved,
  pre_construction: "Pre-construction",
  in_progress: "In progress",
  post_construction: "Post-construction",
  complete: "Complete",
  warranty: "Warranty",
  closed: "Closed",
  declined: JT_JOB_STATUS.declined,
  disqualified: "Disqualified",
};

/** `pending_decision` reads "Pending Decision Day N" when the day is known. */
export function stageLabel(stage: string | null | undefined, decisionDay?: number | null): string {
  const c = normalizeStage(stage);
  if (!c) return stage ? String(stage).replace(/_/g, " ") : "Unknown";
  if (c === "pending_decision" && decisionDay) return `Pending Decision Day ${decisionDay}`;
  return STAGE_LABELS[c];
}

// ---------------------------------------------------------------------------
// Field ownership matrix
// ---------------------------------------------------------------------------

export type OwnershipEntity = "account" | "contact" | "location" | "job" | "vendor_account" | "vendor_bill";
/**
 * C = CRM master, J = JobTread master, LWW = last writer wins with audit (tie goes to JobTread).
 * D = derived: the platform computes the value from its own records, so an inbound JobTread
 * value is neither adopted nor pushed back; JobTread's copy is informational.
 */
export type OwnershipMaster = "C" | "J" | "LWW" | "D";

export interface OwnershipRule {
  entity: OwnershipEntity;
  field: string;
  master: OwnershipMaster;
  jtCustomFieldId?: string;
  note?: string;
}

const CA = JT_CF.customerAccount;
const CC = JT_CF.customerContact;
const VA = JT_CF.vendorAccount;
const LO = JT_CF.location;
const JB = JT_CF.job;

export const OWNERSHIP: readonly OwnershipRule[] = [
  // customer account
  { entity: "account", field: "name", master: "LWW" },
  { entity: "account", field: "primary_contact_id", master: "LWW" },
  { entity: "account", field: "primary_location_id", master: "LWW" },
  { entity: "account", field: "type", master: "C", note: "set at create, immutable after" },
  { entity: "account", field: "status", master: "C", jtCustomFieldId: CA.customerStatus },
  { entity: "account", field: "lead_source", master: "C", jtCustomFieldId: CA.leadSource },
  { entity: "account", field: "referred_by", master: "C", jtCustomFieldId: CA.referredBy },
  { entity: "account", field: "type_of_build", master: "C", jtCustomFieldId: CA.typeOfBuild },
  { entity: "account", field: "job_type", master: "C", jtCustomFieldId: CA.jobType },
  { entity: "account", field: "sales_rep", master: "C", jtCustomFieldId: CA.salesRep },
  { entity: "account", field: "lost_reason", master: "C", jtCustomFieldId: CA.leadLostReason },
  { entity: "account", field: "appointment", master: "C", jtCustomFieldId: CA.appointment },
  { entity: "account", field: "notes", master: "LWW", jtCustomFieldId: CA.notes, note: "append-merge" },
  { entity: "account", field: "archived", master: "J" },

  // vendor / sub account: JobTread is the vendor's record (T46, James, 2026-09-15: "ALL those
  // vendor updates are real ... JT updates are proper"). Until T46 every field here was C, so
  // an edit Courtney made in JobTread was refused locally and a push-back queued to undo it.
  // A platform-side edit still pushes through update_account; nothing pushes back on its own.
  // COI and its expiry are D: the platform reads them off the certificates on file, and a
  // checkbox in JobTread is not a certificate, so neither side overwrites the other.
  { entity: "vendor_account", field: "name", master: "J" },
  { entity: "vendor_account", field: "contacts", master: "J" },
  { entity: "vendor_account", field: "email", master: "J", jtCustomFieldId: VA.email },
  { entity: "vendor_account", field: "phone", master: "J", jtCustomFieldId: VA.phone },
  { entity: "vendor_account", field: "w9", master: "J", jtCustomFieldId: VA.w9 },
  { entity: "vendor_account", field: "coi", master: "D", jtCustomFieldId: VA.coi, note: "computed from certificates on file" },
  { entity: "vendor_account", field: "coi_expires", master: "D", jtCustomFieldId: VA.coiExpires, note: "computed from certificates on file" },
  { entity: "vendor_account", field: "license_number", master: "J", jtCustomFieldId: VA.licenseNumber },
  { entity: "vendor_account", field: "type", master: "J", jtCustomFieldId: VA.type },
  { entity: "vendor_account", field: "specialities", master: "J", jtCustomFieldId: VA.specialities },
  { entity: "vendor_account", field: "payment_terms", master: "J", jtCustomFieldId: VA.paymentTerms },
  { entity: "vendor_account", field: "rating", master: "J", jtCustomFieldId: VA.rating },
  { entity: "vendor_account", field: "website", master: "J", jtCustomFieldId: VA.website },
  { entity: "vendor_account", field: "notes", master: "J", jtCustomFieldId: VA.notes },

  // vendor bills
  { entity: "vendor_bill", field: "create", master: "C", note: "CRM posts from reviewed invoices" },
  { entity: "vendor_bill", field: "payments_status", master: "J", note: "payments and QBO status pulled after create" },

  // contact
  { entity: "contact", field: "name", master: "LWW" },
  { entity: "contact", field: "title", master: "LWW" },
  { entity: "contact", field: "email", master: "LWW", jtCustomFieldId: CC.email },
  { entity: "contact", field: "phone", master: "LWW", jtCustomFieldId: CC.phone, note: "E.164-normalized compare" },
  { entity: "contact", field: "mobile", master: "LWW", jtCustomFieldId: CC.mobile, note: "E.164-normalized compare" },
  { entity: "contact", field: "address", master: "LWW", jtCustomFieldId: CC.address },
  { entity: "contact", field: "notes", master: "LWW", jtCustomFieldId: CC.notes },

  // location
  { entity: "location", field: "address", master: "J", note: "LWW at create, then J (JobTread geocodes)" },
  { entity: "location", field: "entry_code", master: "LWW", jtCustomFieldId: LO.entryCode },
  { entity: "location", field: "notes", master: "LWW", jtCustomFieldId: LO.notes },

  // job
  { entity: "job", field: "name", master: "J" },
  { entity: "job", field: "number", master: "J" },
  { entity: "job", field: "description", master: "J" },
  { entity: "job", field: "status", master: "J", note: "native status" },
  { entity: "job", field: "closed_on", master: "J", note: "CRM writes only on closed" },
  { entity: "job", field: "job_status", master: "J", jtCustomFieldId: JB.jobStatus, note: "JobTread owns the pipeline; the CRM writes it only to advance Pending Decision Day N, or when a person picks a status on the board" },
  { entity: "job", field: "construction_status", master: "J", jtCustomFieldId: JB.constructionStatus, note: "drives deal stage" },
  { entity: "job", field: "job_type", master: "LWW", jtCustomFieldId: JB.jobType, note: "C at create" },
  { entity: "job", field: "sales_rep", master: "LWW", jtCustomFieldId: JB.salesRep, note: "C at create" },
  { entity: "job", field: "estimator", master: "LWW", jtCustomFieldId: JB.estimator, note: "C at create" },
  { entity: "job", field: "customer_po", master: "LWW", jtCustomFieldId: JB.customerPo },
  { entity: "job", field: "money", master: "J", note: "price, cost, line items, documents, payments: read-only mirror" },
];

export function ownershipOf(entity: OwnershipEntity, field: string): OwnershipRule | undefined {
  return OWNERSHIP.find((r) => r.entity === entity && r.field === field);
}

// ---------------------------------------------------------------------------
// Closed sets (app-level enums)
// ---------------------------------------------------------------------------

export const LOST_REASONS = {
  price_too_high: "Price too high",
  budget_financing: "Budget / financing fell through",
  timing_postponed: "Timing, project postponed",
  chose_competitor: "Chose a competitor",
  scope_changed: "Scope changed",
  diy: "Doing it themselves",
  ghosted: "Stopped responding",
  out_of_area: "Outside service area",
  not_qualified: "Not a qualified lead",
  we_declined: "We declined the work",
} as const;
export type LostReason = keyof typeof LOST_REASONS;
export const LOST_REASON_KEYS = Object.keys(LOST_REASONS) as LostReason[];

/**
 * Triage offramp. `disqualified` is NOT a synonym for `lost`: lost is a real prospect who chose
 * otherwise, disqualified is something that never belonged in the pipeline. `spam`, `not_a_lead`
 * and `automated` also mark the account and set do_not_contact on its contacts, so no reminder
 * or review request is ever drafted for them.
 *
 * `automated` was added in T36: one-time passcodes and service notifications sent to the business
 * line were the single largest class of phone junk on the board (six of fifty on 2026-09-09) and
 * they are neither spam nor a pitch — nobody is on the other end to be a lead.
 */
export const DISQUALIFIED_REASONS = {
  spam: "Spam",
  not_a_lead: "Not a lead (newsletter signup, vendor pitch, recruiter)",
  automated: "An automated code or notification",
  out_of_area: "Outside our service area",
  out_of_scope: "Work we do not do",
  duplicate: "Duplicate of another deal",
  unresponsive: "Never responded",
  waste_of_time: "Not worth pursuing",
} as const;
export type DisqualifiedReason = keyof typeof DISQUALIFIED_REASONS;
export const DISQUALIFIED_REASON_KEYS = Object.keys(DISQUALIFIED_REASONS) as DisqualifiedReason[];
/** Reasons that also flag the account and suppress every contact on it. */
export const DISQUALIFIED_SUPPRESS_REASONS: readonly DisqualifiedReason[] = ["spam", "not_a_lead", "automated"];

// ---------------------------------------------------------------------------
// The new-lead cadence (T42)
// ---------------------------------------------------------------------------

/**
 * Four contacts over seven days for a new lead nobody has answered, then a decision.
 *
 * Each step is two things: a task for the owner, and a message DRAFTED into the outbox
 * as `pending_approval`. Nothing is ever sent by this — rule 12 holds, a person presses
 * Send in Work. The alternating channel is deliberate: a text is read and an email can
 * carry the estimate link, and four texts in a row from a contractor reads as a machine.
 *
 * The clock runs from the deal's creation, not from the last step, so a runner that was
 * down for a day catches up to where the lead actually is instead of walking the four
 * steps a tick apart. The runner stops the whole cadence on a first response, a stage
 * move, an ignore, a disqualify, or any inbound activity newer than the deal — see the
 * `lead-cadence` job. This table is only the schedule.
 *
 * `template` is the outbox template's NAME, matched at send time rather than an id,
 * because the rows are seeded by name and a seeded id differs per database.
 */
export const NEW_LEAD_CADENCE = [
  { step: 1, afterMinutes: 60, channel: "sms", template: "Cadence, 1 hour", task: "Call {{first_name}}" },
  { step: 2, afterMinutes: 1440, channel: "email", template: "Cadence, 1 day", task: "Second call to {{first_name}}" },
  { step: 3, afterMinutes: 4320, channel: "sms", template: "Cadence, 3 days", task: "Third contact with {{first_name}}" },
  { step: 4, afterMinutes: 10080, channel: "email", template: "Cadence, 7 days", task: "Final contact with {{first_name}}, then decide" },
] as const;

export type CadenceStep = (typeof NEW_LEAD_CADENCE)[number];
/** The last step in the table. Past it the cadence is finished, not restarted. */
export const CADENCE_LAST_STEP = Math.max(...NEW_LEAD_CADENCE.map((s) => s.step));

/**
 * The next cadence step that is due, or null.
 *
 * Null means one of two different things and the caller does not need to tell them
 * apart: the next step's hour has not come yet, or there is no next step because the
 * lead has had all four. A cadence that reached the end stays ended — it never wraps
 * back to step 1, which is the one bug a modulo here would introduce.
 *
 * `lastStep` is the pointer stored on the deal (`cadence_step`, 0 before the first).
 */
export function cadenceStepDue(createdAt: Date, lastStep: number, now: Date): CadenceStep | null {
  const next = NEW_LEAD_CADENCE.find((s) => s.step > lastStep);
  if (!next) return null;
  const born = createdAt.getTime();
  if (!Number.isFinite(born)) return null;
  return now.getTime() >= born + next.afterMinutes * 60_000 ? next : null;
}

/**
 * Ignored is the other offramp, and it is NOT a stage: the deal keeps whatever stage it had
 * (a JAKS Holdings repair can be in_progress and ignored at the same time). `internal` is
 * applied automatically to JAKS's own entities, matching the weekly scorecard's standing
 * exclusion of any job with "JAKS" in the name. An ignored deal leaves the board, the today
 * counts, the inbox, and every SLA and speed metric; it stays under the Ignored filter.
 */
export const IGNORED_REASONS = {
  internal: "JAKS internal entity (own property, overhead, test job)",
  test: "Test data",
  other: "Ignored by hand",
} as const;
export type IgnoredReason = keyof typeof IGNORED_REASONS;
export const IGNORED_REASON_KEYS = Object.keys(IGNORED_REASONS) as IgnoredReason[];

/**
 * True for JAKS's own accounts and jobs: "JAKS Holdings", "JAKS Management",
 * "JAKS Construction (Internal)", "JAKS Wellington", "JAKS Test", "JAKS Overhead / Admin".
 * Same rule the weekly scorecard uses (exclude any job with "JAKS" in the name). Pure so the
 * website-lead path, the JobTread pull, and the one-time backfill all agree.
 */
export function isInternalName(...names: Array<string | null | undefined>): boolean {
  return names.some((n) => typeof n === "string" && /jaks/i.test(n));
}

/**
 * Decision-age gates for an estimate that is out but undecided (stage estimate_sent or
 * pending_decision). Day 3 and day 7 are check-ins; day 21 is "decide: close or mark lost".
 * The CRM writes a follow-up task for the owner at each crossing. Nothing is sent and
 * nothing is pushed to JobTread.
 */
export const DECISION_GATE_DAYS = [3, 7, 21] as const;
export type DecisionDay = 0 | (typeof DECISION_GATE_DAYS)[number];
export const DECISION_GATE_STAGES: readonly DealStage[] = ["estimate_sent", "pending_decision"];
export const DECISION_GATE_TASKS: Record<(typeof DECISION_GATE_DAYS)[number], string> = {
  3: "Day 3 check-in: call about the estimate",
  7: "Day 7 check-in: call about the estimate, ask what is holding the decision",
  21: "Day 21: decide: close or mark lost",
};

// ---------------------------------------------------------------------------
// Next actions (T42)
// ---------------------------------------------------------------------------

/**
 * Every stage where a deal is still being SOLD: intake, plus the JobTread estimating
 * pipeline up to the decision. `approved` is not here — past the sale the deal belongs
 * to construction, and a missing next action on a job in progress is not a lead anyone
 * dropped. Neither exit is here either.
 *
 * This is the set that must always carry a next action, and the set the Unmanaged queue
 * and the board's chip are counted over.
 */
export const PRESALES_STAGES: readonly DealStage[] = [
  ...INTAKE_STAGES,
  "more_info_needed",
  "estimating",
  "estimate_ready",
  "estimate_sent",
  "pending_decision",
];

/**
 * When a next action is due, as a rule rather than a date.
 *
 * `+1bd` and `+2bd` are BUSINESS days: a lead moved to Contacted on a Friday is
 * followed up on Monday, not chased on Saturday. Turning a rule into an instant needs
 * the company-time business-day helpers in `@jaks/core/day`, which this package cannot
 * import (see the file header: no runtime dependencies, it runs in a browser bundle
 * too), so `nextActionDueAt` lives in `packages/modules/sales/src/next-action.ts` and
 * this file holds only the vocabulary.
 *
 * `visit` means the appointment's own instant: the task IS the visit.
 */
export type NextActionDue = "+5m" | "+1h" | "+1bd" | "+2bd" | "visit";

/**
 * The task written for a deal the moment it enters a stage, keyed by that stage.
 * Approved by James, 2026-09-11; the titles are the exact task text and are not
 * paraphrased anywhere — `{{first_name}}` and `{{address}}` are filled at write time.
 *
 * `new_lead` at five minutes is the same number as the reply clock, on purpose: the
 * task and the clock are the one promise, said twice.
 *
 * `estimate_sent` is an hour, not a day, because the walk-through happens as the
 * estimate is sent or within the hour (James). The day 3, 7 and 21 check-ins that
 * follow are `DECISION_GATE_TASKS` above, written by `crossGate`, which dedupes on its
 * own titles — so the two never collide on the same deal.
 *
 * `pending_decision` has no entry: it is where the gates take over.
 */
export const NEXT_ACTION_DEFAULTS: Record<string, { title: string; due: NextActionDue }> = {
  new_lead: { title: "Respond to {{first_name}}", due: "+5m" },
  contacted: { title: "Follow up with {{first_name}}", due: "+1bd" },
  qualified: { title: "Schedule the site visit with {{first_name}}", due: "+1bd" },
  site_visit: { title: "Site visit at {{address}}", due: "visit" },
  more_info_needed: { title: "Request the missing information from {{first_name}}", due: "+1bd" },
  estimating: { title: "Complete the estimate for {{first_name}}", due: "+2bd" },
  estimate_ready: { title: "Send the estimate to {{first_name}}", due: "+1bd" },
  estimate_sent: { title: "Call {{first_name}} to walk through the estimate", due: "+1h" },
};

/** The task written when a declined deal's return date comes round. */
export const RETURN_TASK_TITLE = "Contact {{first_name}} about resuming the project";

/**
 * How long a declined deal waits before it comes back to the board, by the reason it
 * was declined. Only two reasons have a default: a project that was postponed is a
 * project that starts later, and somebody who stopped responding may answer in a
 * month. Price, a competitor, a scope change, out of area, we declined — those are
 * decisions, not pauses, and a date on them would be a standing lie on the board.
 *
 * A default, not a rule: the Decline sheet shows the date and a person can change or
 * clear it. The deal returns to `new_lead` with `RETURN_TASK_TITLE` and no five-minute
 * reply clock — the clock measures a lead nobody has answered, and this one was.
 */
export const RETURN_DEFAULT_DAYS: Partial<Record<LostReason, number>> = {
  timing_postponed: 90,
  ghosted: 30,
};

/** Whole days since the estimate went out, floored, never negative. */
export function daysSinceEstimate(sentAt: string | Date, now: Date = new Date()): number {
  const sent = typeof sentAt === "string" ? new Date(sentAt) : sentAt;
  const ms = now.getTime() - sent.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.floor(ms / 86_400_000);
}

/** The highest gate (3, 7, 21) at or below the age; 0 before day 3. */
export function decisionDayFor(sentAt: string | Date | null | undefined, now: Date = new Date()): DecisionDay {
  if (!sentAt) return 0;
  const days = daysSinceEstimate(sentAt, now);
  let gate: DecisionDay = 0;
  for (const g of DECISION_GATE_DAYS) if (days >= g) gate = g;
  return gate;
}

export const ACTIVITY_KINDS = [
  "email",
  "sms",
  "call",
  "meeting",
  "note",
  "jt_comment",
  "proposal",
  /** JobTread document other than a sent/decided proposal: drafts, invoices, bid requests, POs, vendor bills. */
  "jt_document",
  /** JobTread schedule / to-do task created or completed. */
  "jt_task",
  /** JobTread daily log entry. */
  "jt_daily_log",
  /** File uploaded to the JobTread job. */
  "jt_file",
  "stage_change",
  "task_done",
  /** T23: the platform's own task trail, one row per change a person made. */
  "task_created",
  "task_edit",
  "task_stage",
  "task_reassign",
  "task_team",
  "task_deleted",
  "file",
  "system",
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export const ACCOUNT_KINDS = ["customer", "vendor", "subcontractor"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

export const CUSTOMER_STATUSES = ["prospect", "qualified", "active", "complete", "lost"] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const VENDOR_STATUSES = ["active", "inactive", "do_not_use"] as const;
export type VendorStatus = (typeof VENDOR_STATUSES)[number];

// ---------------------------------------------------------------------------
// Customer status derivation
//
// `accounts.status` used to be copied from the JobTread "Customer Status" custom field
// (22NwkMqG7eme). JAKS does not maintain that field, so it read "Prospect" for people with
// live jobs (Gary Struble, jt 22Pd4dnmPQZj: estimate out, account said prospect). The field
// is therefore never read back; status is DERIVED from the jobs and deals an account owns,
// and the derived value is pushed to JobTread (CRM is master, see OWNERSHIP).
//
// Precedence, highest first:
//   active    sold work still running, OR an open opportunity on a customer who has bought
//             before (a repeat customer with a new estimate out is active, not complete)
//   qualified an open opportunity and nothing ever sold
//   complete  everything ever sold is finished and nothing is open
//   lost      every outcome is declined or dead, nothing sold, nothing open
//   prospect  no job and no live opportunity: a genuine pre-sale lead
// ---------------------------------------------------------------------------

/**
 * What one job says about the relationship.
 *  won_active   sold and the work is not finished
 *  won_complete sold and finished (construction Complete, or closed out)
 *  open         a live opportunity: estimating / estimate out / awaiting a decision
 *  lost         declined, or closed with nothing ever sold
 */
export const JOB_OUTCOMES = ["won_active", "won_complete", "open", "lost"] as const;
export type JobOutcome = (typeof JOB_OUTCOMES)[number];

export interface JobStatusFacts {
  /** Native JobTread status: created / pending / approved / paid / closed. */
  jtStatus?: string | null;
  /** Job Status custom field text (`JT_JOB_STATUS`). */
  jobStatus?: string | null;
  /** Construction Status custom field text (`JT_CONSTRUCTION_STATUS`). */
  constructionStatus?: string | null;
  closedOn?: string | null;
  /** An approved customer order exists (jobs.won_document_jt_id / won_at). */
  hasWonMarker?: boolean;
}

/**
 * A JobTread job is "sold" on any of: an approved customer order, Job Status = Approved,
 * native status approved or paid. Native `closed` is deliberately NOT proof of a sale: 107
 * of the 155 closed jobs in the live org were closed out with no approved order at all, i.e.
 * abandoned estimates. Closed without a sale is a loss, not a completion.
 */
export function classifyJob(j: JobStatusFacts): JobOutcome {
  const native = (j.jtStatus ?? "").trim().toLowerCase();
  const jobStatus = (j.jobStatus ?? "").trim();
  const construction = (j.constructionStatus ?? "").trim();
  const closed = native === "closed" || !!j.closedOn;
  const hardSold = !!j.hasWonMarker || native === "approved" || native === "paid";
  // The Job Status field is set by hand and stays "Approved" on jobs that were later cancelled
  // (Mustafa Affify #10607: approval withdrawn over insurance, closed mid-construction). On its
  // own it only proves a sale when the work visibly happened: construction reached
  // Post-Construction or Complete. Closed earlier than that with no approved order is abandoned.
  const workHappened =
    construction === JT_CONSTRUCTION_STATUS.complete || construction === JT_CONSTRUCTION_STATUS.postConstruction;
  const softSold = jobStatus === JT_JOB_STATUS.approved && (!closed || workHappened);
  const sold = hardSold || softSold;
  if (sold) return closed || construction === JT_CONSTRUCTION_STATUS.complete ? "won_complete" : "won_active";
  if (jobStatus === JT_JOB_STATUS.declined) return "lost";
  if (closed) return "lost";
  return "open";
}

/** A deal that has no job yet still says something: it is the CRM-side half of the pipeline. */
export interface DealStatusFacts {
  stage: DealStage;
  /** True when the deal already has a job row; its job carries the evidence instead. */
  hasJob?: boolean;
}

/** Outcome of a deal with no job. `lead` means "too early to count as an opportunity". */
export type DealOutcome = JobOutcome | "lead";

export function classifyDeal(stage: DealStage): DealOutcome {
  if (stage === "declined" || stage === "disqualified") return "lost";
  if (stage === "new_lead" || stage === "contacted") return "lead";
  if (isPreWon(stage)) return "open";
  if (stage === "complete" || stage === "closed") return "won_complete";
  return "won_active";
}

export interface AccountStatusFacts {
  kind: AccountKind;
  /** Live (not soft-deleted) jobs on the account. */
  jobs?: readonly JobStatusFacts[];
  /** Live deals on the account; ones that already have a job are ignored (the job wins). */
  deals?: readonly DealStatusFacts[];
}

export interface DerivedCustomerStatus {
  status: CustomerStatus;
  /** Human-readable justification, written to the sync log and the fix-statuses table. */
  reason: string;
  counts: Record<JobOutcome | "lead", number>;
}

/**
 * Derive a customer account's status from its jobs and its jobless deals.
 * Returns null for vendors and subcontractors: their status vocabulary is a different set
 * (VENDOR_STATUSES) and nothing here may touch it.
 */
export function deriveCustomerStatus(f: AccountStatusFacts): DerivedCustomerStatus | null {
  if (f.kind !== "customer") return null;
  const counts: Record<JobOutcome | "lead", number> = { won_active: 0, won_complete: 0, open: 0, lost: 0, lead: 0 };
  for (const j of f.jobs ?? []) counts[classifyJob(j)]++;
  for (const d of f.deals ?? []) {
    if (d.hasJob) continue;
    counts[classifyDeal(d.stage)]++;
  }
  const everSold = counts.won_active + counts.won_complete;
  const n = (k: JobOutcome | "lead", noun: string) => `${counts[k]} ${noun}${counts[k] === 1 ? "" : "s"}`;

  if (counts.won_active > 0) {
    return { status: "active", reason: `${n("won_active", "sold job")} still in construction`, counts };
  }
  if (counts.open > 0 && everSold > 0) {
    return { status: "active", reason: `repeat customer: ${n("won_complete", "finished job")}, ${n("open", "open opportunity")}`, counts };
  }
  if (counts.open > 0) {
    return { status: "qualified", reason: `${n("open", "open opportunity")}, nothing sold yet`, counts };
  }
  if (counts.won_complete > 0) {
    return { status: "complete", reason: `${n("won_complete", "sold job")} finished, nothing open`, counts };
  }
  if (counts.lost > 0) {
    return { status: "lost", reason: `${n("lost", "declined or dead job")}, nothing sold or open`, counts };
  }
  return { status: "prospect", reason: counts.lead > 0 ? `${n("lead", "early-stage lead")}, no job` : "no jobs and no deals", counts };
}

/**
 * `sub_agreement` is the per-trade subcontract, which is not the same paper as
 * `msa`: a master services agreement is signed once and a subcontract is signed
 * per scope. T8 collects both, so both are named. @jaks/engine-acord25 carries
 * its own copy on purpose (an engine takes no workspace dependency); the two
 * are asserted equal in packages/modules/vendors/src/packet.test.ts, which is
 * the one place that already imports both.
 */
export const COMPLIANCE_DOC_TYPES = ["coi_gl", "coi_wc", "coi_auto", "coi_umbrella", "license", "w9", "msa", "sub_agreement", "other"] as const;
export type ComplianceDocType = (typeof COMPLIANCE_DOC_TYPES)[number];

/**
 * Compliance state vocabulary.
 *
 * `missing` and `missing_from_certificate` are deliberately different states, and the
 * difference is the whole point of the workers comp tracking. `missing` means JAKS holds
 * nothing at all for that coverage. `missing_from_certificate` means JAKS holds a
 * certificate for this vendor and that certificate does not carry the coverage: the sub
 * looks documented, the file drawer looks full, and the workers comp audit still charges
 * JAKS premium for them. That case has to be loud, not silent.
 *
 * `waived` is only honest when someone recorded why and put their name to it, so a waived
 * row without `waiver_reason` and `waived_by` is treated as no proof at all.
 *
 * `unreadable_scan` means a file is on hand but nothing could be read from it
 * deterministically (a camera scan or fax with no text layer, or a certificate whose form
 * labels are an image so the values cannot be tied to a coverage). It is never a guess: a
 * human has to open the file and key the row, or ask the vendor for a native PDF.
 */
export const COMPLIANCE_STATUSES = ["valid", "expiring", "expired", "missing", "missing_from_certificate", "unreadable_scan", "waived"] as const;
export type ComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];

/** Where a compliance record came from, so an auditor can be walked back to the paper. */
/** `jobtread_form` is a JobTread Forms submission; `source_ref` is then the submission id. */
export const COMPLIANCE_SOURCES = ["manual", "jobtread_file", "jobtread_cf", "jobtread_form", "drive", "gmail", "import"] as const;
export type ComplianceSource = (typeof COMPLIANCE_SOURCES)[number];

/** Doc types that carry an expiry date. A W-9 does not expire; a certificate does. */
export const EXPIRING_DOC_TYPES: readonly ComplianceDocType[] = ["coi_gl", "coi_wc", "coi_auto", "coi_umbrella", "license"];

/* ------------------------------------------------- compliance collection */

/**
 * The paperwork packets the platform collects on JobTread Forms.
 *
 * A packet is a REQUEST for documents, not the documents themselves. It names
 * what is being asked for; `COMPLIANCE_DOC_TYPES` names what comes back. The
 * two lists deliberately do not match: one qualification packet carries a W-9,
 * two certificates and a signature, and a renewal chase carries one
 * certificate.
 */
export const COMPLIANCE_PACKETS = ["qualification", "coi_renewal", "w9", "msa", "sub_agreement"] as const;
export type CompliancePacket = (typeof COMPLIANCE_PACKETS)[number];

/** What each packet is meant to satisfy, so a returned submission settles the right rows. */
export const PACKET_DOC_TYPES: Record<CompliancePacket, readonly ComplianceDocType[]> = {
  qualification: ["w9", "coi_gl", "coi_wc", "license"],
  coi_renewal: ["coi_gl", "coi_wc"],
  w9: ["w9"],
  msa: ["msa"],
  sub_agreement: ["sub_agreement"],
};

/**
 * The lifecycle of one collection request.
 *
 * The gate is between `pending_approval` and `approved`, and it is a person.
 * `sent` means the request reached JobTread and therefore reached the vendor;
 * nothing may be `sent` that was not first `approved`, and the database says so
 * as well as the code (see migration 0025). `refused` is the write guard
 * declining outside production, which is the guard working rather than an
 * error: the request keeps its reason and can be approved again in production.
 */
export const COMPLIANCE_REQUEST_STATUSES = [
  "draft", "pending_approval", "approved", "sent", "submitted", "reviewed", "cancelled", "refused",
] as const;
export type ComplianceRequestStatus = (typeof COMPLIANCE_REQUEST_STATUSES)[number];

/** Statuses that still hold a place in the queue, so a chase cannot pile duplicates on one vendor. */
export const LIVE_COMPLIANCE_REQUEST_STATUSES: readonly ComplianceRequestStatus[] = [
  "draft", "pending_approval", "approved", "sent", "submitted", "refused",
];

/**
 * Kansas: a general contractor can be held the statutory employer of an uninsured sub's
 * injured worker (KSA 44-503), and the annual workers comp audit charges the GC premium for
 * every sub who cannot produce a certificate. Workers comp is therefore required of every
 * subcontractor. Suppliers who only deliver material are tracked but not required.
 */
export const WC_REQUIRED_KINDS: readonly AccountKind[] = ["subcontractor"];

export const INVOICE_KINDS = ["invoice", "order_confirmation", "credit_memo", "statement", "receipt"] as const;
export type InvoiceKind = (typeof INVOICE_KINDS)[number];

export const INVOICE_STATUSES = [
  "ingested",
  "needs_review",
  "assigned",
  "posted_to_jt",
  "disputed",
  "paid",
  "void",
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const OUTBOX_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "queued",
  "sent",
  "delivered",
  "failed",
  "skipped",
] as const;
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

export const SYNC_STATES = ["local", "pending_push", "synced", "conflict", "jt_deleted"] as const;
export type SyncState = (typeof SYNC_STATES)[number];

export const SYNC_OUTBOX_STATUSES = ["pending", "in_flight", "done", "failed", "dead"] as const;
export type SyncOutboxStatus = (typeof SYNC_OUTBOX_STATUSES)[number];

export const PUSH_STATES = ["queued", "partial", "done", "failed"] as const;
export type PushState = (typeof PUSH_STATES)[number];

export const APPROVAL_MODES = ["human", "auto"] as const;
export type ApprovalMode = (typeof APPROVAL_MODES)[number];

export const MILESTONE_KINDS = [
  "consult_held",
  "design_agreement_signed",
  "walkthrough_held",
  "proposal_issued",
  "contract_signed",
  "permit_issued",
  "rough_in_passed",
  "final_inspection",
  "co_issued",
  "draw_requested",
  "draw_funded",
  "warranty_start",
] as const;
export type MilestoneKind = (typeof MILESTONE_KINDS)[number];

export const DOCUMENT_KINDS = [
  "design_agreement",
  "precon_agreement",
  "contract",
  "change_order",
  "lien_waiver",
  "coi",
  "w9",
  "license",
  "invoice",
  "statement",
  "photo",
  "other",
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const TASK_STATUSES = ["open", "done", "cancelled", "snoozed"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/**
 * Which JobTread record a task hangs off.
 *
 * The join key is the ACCOUNT, not the job: a customer has many jobs and
 * locations, and a task created before a JobTread job exists has nowhere else
 * to go. `organization` is JobTread's fourth target and the platform never
 * creates one; it is here because the mirror reads them and a value the type
 * cannot express would be dropped silently.
 */
export const JT_TASK_TARGETS = ["job", "account", "organization"] as const;
export type JtTaskTarget = (typeof JT_TASK_TARGETS)[number];

/**
 * Where a task stands against JobTread. Deliberately the same four words the
 * account, contact, location and job rows already use, rather than a fifth
 * vocabulary for the same idea.
 */
export const TASK_SYNC_STATES = ["pending", "synced", "failed", "conflict"] as const;
export type TaskSyncState = (typeof TASK_SYNC_STATES)[number];

/**
 * Which side owns a task, derived rather than stored: a task with a JobTread
 * id is JobTread's, and one without is the platform's. Ownership switches at
 * JobTread job creation, so it is a fact about `jtTaskId` and never a column.
 */
export type TaskOwner = "platform" | "jobtread";
export function taskOwnerOf(jtTaskId: string | null | undefined): TaskOwner {
  return jtTaskId ? "jobtread" : "platform";
}

// ---------------------------------------------------------------------------
// Task stages, checklists and the board (T23)
// ---------------------------------------------------------------------------

/**
 * What a stage MEANS, as opposed to what it is called.
 *
 * Names belong to the admin: To-Do, In-Progress, Blocked and Done are only
 * the seed, and a stage can be renamed, added or deleted from the Tasks
 * screen. The kind is what the platform reads: which stage a new task lands
 * in, which one means finished, and where a JobTread task's progress puts it
 * on a board it can never be dragged across.
 */
export const STAGE_KINDS = ["todo", "in_progress", "blocked", "done"] as const;
export type StageKind = (typeof STAGE_KINDS)[number];
export const STAGE_KIND_LABELS: Record<StageKind, string> = {
  todo: "To do", in_progress: "In progress", blocked: "Blocked", done: "Done",
};
export function isStageKind(v: unknown): v is StageKind {
  return typeof v === "string" && (STAGE_KINDS as readonly string[]).includes(v);
}

/** The statuses a person may write. `cancelled` is read-only history: deleting a task is the platform's cancel. */
export const WRITABLE_TASK_STATUSES = ["open", "done", "snoozed"] as const satisfies readonly TaskStatus[];

/**
 * The seeded stages, by id, so the local seed and the tests can name them.
 * The routes never do: they resolve the default by `is_default` and the
 * finishing stage by `kind`, because an admin may have replaced every one of
 * these by the time a task is created.
 */
export const SEED_STAGE_IDS = {
  todo: "00000000-0000-4000-8000-00000000a001",
  inProgress: "00000000-0000-4000-8000-00000000a002",
  blocked: "00000000-0000-4000-8000-00000000a003",
  done: "00000000-0000-4000-8000-00000000a004",
} as const;

export interface TaskStage {
  id: string;
  name: string;
  kind: StageKind;
  position: number;
  isDefault: boolean;
  taskCount?: number;
}

/** Where JobTread's own 0..1 progress puts a mirrored task on the platform's board. */
export function stageKindForProgress(p: number | string | null | undefined): StageKind {
  const n = p === null || p === undefined || p === "" ? 0 : Number(p);
  if (!Number.isFinite(n) || n <= 0) return "todo";
  return n >= 1 ? "done" : "in_progress";
}

/**
 * The one rule that keeps `status` and the stage telling the same story: a
 * task is done exactly when its stage is the done kind.
 *
 * Callers pass what they know and get back what to write. Moving into a done
 * stage completes; leaving one reopens; a snooze survives in any stage that is
 * not done. Asking for `done` while pointing at a stage that is not the done
 * kind is a caller error, because the caller is the one who chose the stage.
 */
export function reconcileStageStatus(i: {
  currentStatus: TaskStatus;
  targetKind: StageKind;
  requestedStatus?: TaskStatus | null;
}): { status: TaskStatus; completed: "set" | "clear" | "keep" } {
  if (i.targetKind === "done") {
    return { status: "done", completed: i.currentStatus === "done" ? "keep" : "set" };
  }
  if (i.requestedStatus === "done") throw new TypeError("A done status needs a stage of the done kind.");
  const status = i.requestedStatus ?? (i.currentStatus === "done" ? "open" : i.currentStatus);
  return { status, completed: i.currentStatus === "done" ? "clear" : "keep" };
}

export const MAX_CHECKLIST_ITEMS = 50;
export const MAX_CHECKLIST_TEXT = 200;

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

/**
 * A checklist as the API accepts it: at most fifty items, each a non-empty
 * text of at most two hundred characters and a done flag. An item's id is kept
 * when the client sends one and minted when it does not, so a screen can send
 * back what it was given plus one new line. Throws a TypeError that names the
 * fault, which the route turns into a 400.
 */
export function normalizeChecklist(
  raw: unknown,
  mintId: () => string = () => globalThis.crypto.randomUUID(),
): ChecklistItem[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) throw new TypeError("checklist must be a list.");
  if (raw.length > MAX_CHECKLIST_ITEMS) throw new TypeError(`checklist holds at most ${MAX_CHECKLIST_ITEMS} items.`);
  const seen = new Set<string>();
  return raw.map((item, n) => {
    if (!item || typeof item !== "object") throw new TypeError(`checklist item ${n + 1} is not an object.`);
    const o = item as Record<string, unknown>;
    const text = typeof o.text === "string" ? o.text.trim() : "";
    if (!text) throw new TypeError(`checklist item ${n + 1} has no text.`);
    if (text.length > MAX_CHECKLIST_TEXT) throw new TypeError(`checklist item ${n + 1} is longer than ${MAX_CHECKLIST_TEXT} characters.`);
    let id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : mintId();
    while (seen.has(id)) id = mintId();
    seen.add(id);
    return { id, text, done: o.done === true };
  });
}

export const CONSENT_STATUSES = ["opted_in", "opted_out", "unknown"] as const;
export type ConsentStatus = (typeof CONSENT_STATUSES)[number];

export const CONSENT_SOURCES = ["website_form", "verbal", "sms_keyword", "unsubscribe_link", "import"] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

export const QUALIFICATION_STATUSES = ["prospect", "qualified", "approved", "probation", "do_not_use"] as const;
export type QualificationStatus = (typeof QUALIFICATION_STATUSES)[number];

export const ORDER_KINDS = ["material_po", "sub_contract", "rental", "change_order"] as const;
export type OrderKind = (typeof ORDER_KINDS)[number];

export const CHANNELS = ["email", "sms"] as const;
export type Channel = (typeof CHANNELS)[number];

export const TEMPLATE_CATEGORIES = ["transactional", "marketing"] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

/** Inbound SMS keywords that mirror to an opt-out (case-insensitive, whole message). */
export const SMS_OPT_OUT_KEYWORDS = ["STOP", "UNSUBSCRIBE", "CANCEL", "END", "QUIT"] as const;

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

export interface SplitPhone {
  e164: string | null;
  ext?: string;
}

const EXT_RE = /(?:\s*(?:ext\.?|extension|x|#)\s*[:.]?\s*(\d{1,6}))\s*$/i;

/**
 * Split a raw phone string into an E.164 number and an optional extension.
 * Handles 10-digit US numbers, 11-digit numbers with a leading 1, and an
 * already-formatted +1 number. Anything else returns `e164: null`.
 */
export function splitPhone(raw: string, defaultRegion: "US" = "US"): SplitPhone {
  if (typeof raw !== "string") return { e164: null };
  let s = raw.trim();
  if (!s) return { e164: null };

  let ext: string | undefined;
  const m = s.match(EXT_RE);
  if (m) {
    ext = m[1];
    s = s.slice(0, m.index).trim();
  }

  const hasPlus = s.startsWith("+");
  const digits = s.replace(/\D/g, "");
  if (!digits) return { e164: null };

  if (defaultRegion === "US") {
    let national: string | null = null;
    if (digits.length === 10) national = digits;
    else if (digits.length === 11 && digits.startsWith("1")) national = digits.slice(1);
    else if (hasPlus) {
      // Non-US international number: accept only if it is a plausible length.
      if (digits.length >= 8 && digits.length <= 15) {
        return ext ? { e164: `+${digits}`, ext } : { e164: `+${digits}` };
      }
      return { e164: null };
    }
    if (!national) return { e164: null };
    // NANP: area code and exchange cannot start with 0 or 1.
    if (/^[01]/.test(national) || /^[01]/.test(national.slice(3))) return { e164: null };
    const e164 = `+1${national}`;
    return ext ? { e164, ext } : { e164 };
  }

  return { e164: null };
}

export function toE164(raw: string, defaultRegion: "US" = "US"): string | null {
  return splitPhone(raw, defaultRegion).e164;
}

/**
 * A phone number laid out for a person: "(316) 555-0142" (T45).
 *
 * Numbers are STORED as E.164 (+13165550142): that is the identity key, what Quo and JobTread
 * exchange, and what a tel: link dials. Nobody reads E.164 comfortably, so everything a person sees
 * goes through this. A US number arrives in any of the three shapes the platform holds: E.164, ten
 * digits, or eleven with a leading 1 (the roster and JobTread carry the last two), with or without
 * punctuation. Anything else, such as a non-US number or free text, comes back exactly as it was,
 * and nothing comes back null.
 */
export function formatPhone(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  const digits = s.replace(/\D/g, "");
  let national: string | null = null;
  if (s.startsWith("+")) {
    if (digits.length === 11 && digits.startsWith("1")) national = digits.slice(1);
  } else if (digits.length === 10) national = digits;
  else if (digits.length === 11 && digits.startsWith("1")) national = digits.slice(1);
  if (!national) return s;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/** Every US E.164 number inside free text, laid out the same way; the rest of the text untouched. */
export function formatPhonesIn(text: string): string {
  return text.replace(/(^|[^\d+])\+1(\d{3})(\d{3})(\d{4})(?!\d)/g, (_m, before: string, a: string, b: string, c: string) => `${before}(${a}) ${b}-${c}`);
}

/**
 * The name the phone line gives an account and contact it minted for a number nobody knows, and
 * the title of the deal it opens. The prefix is what every reader of the placeholder keys on
 * (normalizePartyName, the website door's rename, the JobTread pull's adoption); the number is for
 * the person looking at the board. Migration 0047 wrote the names minted before this into this shape.
 */
export function unknownCallerName(e164: string): string {
  return `Unknown caller ${formatPhone(e164) ?? e164}`;
}

export function phoneLeadTitle(e164: string): string {
  return `${formatPhone(e164) ?? e164}: inbound phone lead`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Lowercase, trim, strip internal whitespace and mailto:, and validate shape. */
export function normEmail(raw: string): string | null {
  if (typeof raw !== "string") return null;
  let s = raw.trim().toLowerCase();
  if (s.startsWith("mailto:")) s = s.slice(7);
  const angle = s.match(/<([^>]+)>/);
  if (angle?.[1]) s = angle[1];
  s = s.replace(/\s+/g, "");
  if (!s || !EMAIL_RE.test(s)) return null;
  if (s.includes("..")) return null;
  return s;
}

/**
 * T39. The reaches a party can be matched or claimed on: one normalized email and every phone in
 * E.164, each once. This is the shape `resolveParty` reads and `claimIdentityKeys` writes, and it
 * is pure so every door (website, Quo, the JobTread pull, the manual form) normalizes the same way.
 */
export interface PartyKeys {
  email: string | null;
  phones: string[];
}

export function partyKeysOf(p: {
  email?: string | null;
  phones?: ReadonlyArray<string | null | undefined>;
}): PartyKeys {
  const email = p.email ? normEmail(p.email) : null;
  const phones: string[] = [];
  for (const raw of p.phones ?? []) {
    if (!raw) continue;
    const e164 = toE164(raw);
    if (e164 && !phones.includes(e164)) phones.push(e164);
  }
  return { email, phones };
}

/**
 * A party's name as the fuzzy leg compares it: lowercase, punctuation gone, one space between
 * words. Null when there is nothing left, and null for the "Unknown caller" placeholder the phone
 * line mints, because two placeholders sharing a name is not two people sharing a name.
 */
export function normalizePartyName(name: string | null | undefined): string | null {
  if (typeof name !== "string") return null;
  const trimmed = name.trim();
  if (!trimmed || /^unknown caller/i.test(trimmed)) return null;
  const out = trimmed
    .toLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  return out || null;
}

// ---------------------------------------------------------------------------
// JAKS's own reaches (T44)
// ---------------------------------------------------------------------------

/**
 * The `core.settings` key for the company's own phone lines, shared addresses and email domains.
 *
 * Why it exists: on 2026-09-11 a test customer was created with the JAKS main line as her phone
 * and vendors@jaks.construction as her email, and within hours a real customer's call
 * transcript, a bank passcode and a vendor statement were filed on her. The phone line had
 * been doing the same to JAKS Holdings since 2026-08-27, because the SSM parameter naming our
 * own number was never created and the ingest took the main line for the other party. A reach
 * that belongs to JAKS is never a customer's and never a vendor's, at any door.
 */
export const OWN_REACHES_KEY = "own_reaches";

/** What the setting stores. Employees are not in it: `core.people` is read live beside it. */
export interface OwnReachesSetting {
  /** The JAKS phone lines, E.164, the main line first. */
  phones: string[];
  /** Whole domains no customer or vendor contact may carry, lowercase and without "@". */
  emailDomains: string[];
  /** Single shared addresses outside those domains. */
  emails: string[];
}

/** Everything a door checks: the setting, plus every active employee's phone and email. */
export interface OwnReaches {
  /** E.164 of each JAKS line, to the words that name it. */
  lines: ReadonlyMap<string, string>;
  /** E.164 of each active employee's phone, to "Name (employee)". */
  peoplePhones: ReadonlyMap<string, string>;
  /** Normalized address, to its owner: a shared address from the setting or an employee. */
  emails: ReadonlyMap<string, string>;
  /** Lowercase domain, to "the domain (company domain)". */
  emailDomains: ReadonlyMap<string, string>;
}

export interface OwnReach {
  kind: "phone" | "email" | "domain";
  /** The normalized reach that matched: E.164, an address, or the address whose domain matched. */
  value: string;
  /** Who it belongs to, in words a person reads: "the JAKS main line", "Cara McClenny (employee)". */
  owner: string;
}

const DOMAIN_RE = /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/;

/** A domain as the setting keeps it: lowercase, no "@", no surrounding space. Null when it is not one. */
export function normalizeEmailDomain(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const d = raw.trim().toLowerCase().replace(/^@/, "");
  return DOMAIN_RE.test(d) ? d : null;
}

/**
 * The stored value read leniently: whatever is not a phone, a domain or an address is left out
 * rather than thrown, because a door reading this must never fail on it. The Settings PUT is
 * where a bad value is refused.
 */
export function parseOwnReachesSetting(raw: unknown): OwnReachesSetting {
  const v = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);
  const uniq = (xs: Array<string | null>): string[] => [...new Set(xs.filter((x): x is string => !!x))];
  return {
    phones: uniq(list(v.phones).map((p) => (typeof p === "string" ? toE164(p) : null))),
    emailDomains: uniq(list(v.emailDomains).map(normalizeEmailDomain)),
    emails: uniq(list(v.emails).map((e) => (typeof e === "string" ? normEmail(e) : null))),
  };
}

/**
 * The Settings save, strict where the reader is lenient: every entry must be what it claims, and
 * there must be at least one phone line, because a call cannot be attributed without one and the
 * ingest refuses to guess (T44). The main line is whichever comes first.
 */
export function validateOwnReachesSetting(input: unknown): { ok: true; value: OwnReachesSetting } | { ok: false; errors: string[] } {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, errors: ["Send phones, emailDomains and emails as lists."] };
  }
  const v = input as Record<string, unknown>;
  const errors: string[] = [];
  const listOf = (key: string): unknown[] => {
    const x = v[key] ?? [];
    if (!Array.isArray(x)) { errors.push(`${key} must be a list.`); return []; }
    return x;
  };
  const phones: string[] = [];
  for (const p of listOf("phones")) {
    const e164 = typeof p === "string" ? toE164(p) : null;
    if (!e164) errors.push(`"${String(p)}" is not a phone number.`);
    else if (!phones.includes(e164)) phones.push(e164);
  }
  const emailDomains: string[] = [];
  for (const d of listOf("emailDomains")) {
    const domain = normalizeEmailDomain(d);
    if (!domain) errors.push(`"${String(d)}" is not an email domain.`);
    else if (!emailDomains.includes(domain)) emailDomains.push(domain);
  }
  const emails: string[] = [];
  for (const e of listOf("emails")) {
    const email = typeof e === "string" ? normEmail(e) : null;
    if (!email) errors.push(`"${String(e)}" is not an email address.`);
    else if (!emails.includes(email)) emails.push(email);
  }
  if (!phones.length && !errors.some((x) => x.startsWith("phones"))) {
    errors.push("At least one JAKS phone line is required: without it no call can be attributed to anybody.");
  }
  return errors.length ? { ok: false, errors } : { ok: true, value: { phones, emailDomains, emails } };
}

/** The lookup a door checks, from the setting and the active people it was read beside. */
export function ownReachesOf(
  setting: OwnReachesSetting | null | undefined,
  people: ReadonlyArray<{ name?: string | null; email?: string | null; phone?: string | null }> = [],
): OwnReaches {
  const lines = new Map<string, string>();
  const peoplePhones = new Map<string, string>();
  const emails = new Map<string, string>();
  const emailDomains = new Map<string, string>();
  (setting?.phones ?? []).forEach((p, i) => {
    const e164 = toE164(p);
    if (e164 && !lines.has(e164)) lines.set(e164, i === 0 ? "the JAKS main line" : "a JAKS phone line");
  });
  for (const d of setting?.emailDomains ?? []) {
    const domain = normalizeEmailDomain(d);
    if (domain) emailDomains.set(domain, `${domain} (the company domain)`);
  }
  for (const e of setting?.emails ?? []) {
    const email = normEmail(e);
    if (email) emails.set(email, `${email} (a JAKS address)`);
  }
  for (const person of people) {
    const who = `${(person.name ?? "").trim() || "a JAKS employee"} (employee)`;
    const email = person.email ? normEmail(person.email) : null;
    if (email && !emails.has(email)) emails.set(email, who);
    const phone = person.phone ? toE164(person.phone) : null;
    if (phone && !lines.has(phone) && !peoplePhones.has(phone)) peoplePhones.set(phone, who);
  }
  return { lines, peoplePhones, emails, emailDomains };
}

export const NO_OWN_REACHES: OwnReaches = ownReachesOf(null);

/** True when the setting names at least one JAKS line: the one fact a call cannot be read without. */
export function ownLinesConfigured(reaches: OwnReaches): boolean {
  return reaches.lines.size > 0;
}

/**
 * Whether a phone or an email belongs to JAKS. Pure and total: raw input is normalized here, so a
 * caller holding "(316) 749-8199" or "Cara@JAKS.construction" asks the same question as one
 * holding the stored form. A phone is checked before an email; an employee's own address is named
 * before the domain it sits under, because "Cara McClenny (employee)" is the more useful answer.
 */
export function ownReachOf(reaches: OwnReaches, reach: { phone?: string | null; email?: string | null }): OwnReach | null {
  const phone = reach.phone ? toE164(reach.phone) : null;
  if (phone) {
    const owner = reaches.lines.get(phone) ?? reaches.peoplePhones.get(phone);
    if (owner) return { kind: "phone", value: phone, owner };
  }
  const email = reach.email ? normEmail(reach.email) : null;
  if (email) {
    const owner = reaches.emails.get(email);
    if (owner) return { kind: "email", value: email, owner };
    const domain = email.slice(email.lastIndexOf("@") + 1);
    const byDomain = reaches.emailDomains.get(domain);
    if (byDomain) return { kind: "domain", value: email, owner: byDomain };
  }
  return null;
}

/** Every own reach among a party's keys, each once. */
export function ownReachesIn(reaches: OwnReaches, keys: PartyKeys): OwnReach[] {
  const out: OwnReach[] = [];
  const hit = keys.email ? ownReachOf(reaches, { email: keys.email }) : null;
  if (hit) out.push(hit);
  for (const p of keys.phones) {
    const h = ownReachOf(reaches, { phone: p });
    if (h) out.push(h);
  }
  return out;
}

/** A party's keys with every JAKS reach taken out: what a door may match on, write, or claim. */
export function withoutOwnReaches(reaches: OwnReaches, keys: PartyKeys): PartyKeys {
  return {
    email: keys.email && ownReachOf(reaches, { email: keys.email }) ? null : keys.email,
    phones: keys.phones.filter((p) => !ownReachOf(reaches, { phone: p })),
  };
}

/** A US number as a person writes it, "(316) 749-8199"; anything else as given. */
export function displayPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/** The sentence a form shows under the field. */
export function ownReachRefusal(reach: OwnReach): string {
  const what = reach.kind === "phone" ? displayPhone(reach.value) : reach.value;
  return `${what} is ${reach.owner}. A customer or contact cannot be given a JAKS phone number or address.`;
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

const MERGE_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

function lookup(ctx: Record<string, unknown>, path: string): unknown {
  let cur: unknown = ctx;
  for (const part of path.split(".")) {
    if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/** Replace `{{a.b.c}}` merge fields from ctx; missing or null values render as empty string. */
export function renderTemplate(body: string, ctx: Record<string, unknown>): string {
  return body.replace(MERGE_RE, (_m, path: string) => {
    const v = lookup(ctx, path);
    if (v === undefined || v === null) return "";
    if (v instanceof Date) return v.toISOString();
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  });
}

/** Merge field paths referenced in a template body. */
export function mergeFields(body: string): string[] {
  const out = new Set<string>();
  for (const m of body.matchAll(MERGE_RE)) if (m[1] !== undefined) out.add(m[1]);
  return [...out];
}

// GSM 03.38 basic character set plus the extension table (which costs two septets each).
const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "^{}\\[~]|€";

export function isGsm7(text: string): boolean {
  for (const ch of text) {
    if (!GSM7_BASIC.includes(ch) && !GSM7_EXT.includes(ch)) return false;
  }
  return true;
}

/** Number of SMS segments: GSM-7 160 single / 153 multipart, UCS-2 70 / 67. */
export function smsSegments(text: string): number {
  if (!text) return 0;
  if (isGsm7(text)) {
    let septets = 0;
    for (const ch of text) septets += GSM7_EXT.includes(ch) ? 2 : 1;
    if (septets <= 160) return 1;
    return Math.ceil(septets / 153);
  }
  // UCS-2 counts UTF-16 code units; an emoji outside the BMP is two.
  const units = text.length;
  if (units <= 70) return 1;
  return Math.ceil(units / 67);
}

export interface TemplateInput {
  channel: Channel;
  category: TemplateCategory;
  subject?: string;
  body: string;
  /** True when this template is the first message of a cadence (SMS needs an opt-out line). */
  firstTouch?: boolean;
}

const OPT_OUT_RE = /\b(STOP|UNSUBSCRIBE|opt[\s-]?out)\b/i;

/** Returns a list of human-readable errors; empty means valid. */
export function templateValidation(t: TemplateInput): string[] {
  const errors: string[] = [];
  const body = t.body ?? "";
  if (!body.trim()) errors.push("Body is required.");

  if (t.channel === "email") {
    if (!t.subject || !t.subject.trim()) errors.push("Email templates need a subject.");
    if (t.category === "marketing") {
      if (!body.includes("{{links.unsubscribe}}")) {
        errors.push("Marketing email must include {{links.unsubscribe}}.");
      }
      if (!body.includes("{{org.postal_address}}")) {
        errors.push("Marketing email must include {{org.postal_address}} (CAN-SPAM address block).");
      }
    }
  }

  if (t.channel === "sms") {
    if (t.subject && t.subject.trim()) errors.push("SMS templates do not use a subject.");
    if ((t.firstTouch || t.category === "marketing") && !OPT_OUT_RE.test(body)) {
      errors.push('First-touch SMS must include an opt-out phrase such as "Reply STOP to opt out".');
    }
    const rendered = body.replace(MERGE_RE, "xxxxxxxxxx");
    const segs = smsSegments(rendered);
    if (segs > 3) errors.push(`SMS renders to about ${segs} segments; keep it to 3 or fewer.`);
  }

  for (const m of body.matchAll(/\{\{([^}]*)\}\}/g)) {
    if (m[1] !== undefined && !/^\s*[a-zA-Z0-9_.]+\s*$/.test(m[1])) errors.push(`Malformed merge field: {{${m[1]}}}`);
  }
  const open = (body.match(/\{\{/g) ?? []).length;
  const close = (body.match(/\}\}/g) ?? []).length;
  if (open !== close) errors.push("Unbalanced merge field braces.");

  return errors;
}

/* ------------------------------------------------------------------------------------------
 * JobTread option fields: the vocabulary, not the lists
 *
 * Option custom fields reject any value outside their configured list, and Pave fails the
 * WHOLE mutation when one is wrong (hit live 2026-08-21: "other" is not a valid option for
 * the Lead Source custom field). The CRM keeps its own vocabulary, so every option-typed CF
 * gets an explicit map, and anything unmappable is DROPPED rather than guessed.
 *
 * The lists are JobTread's and are read from JobTread (@jaks/engine-jobtread option-lists.ts,
 * T38). This file used to hold a second hand copy of them; it and the engine's copy both went
 * stale when Cara McClenny's name changed on the job fields. There is no copy now.
 * ---------------------------------------------------------------------------------------- */

/** Account Job Type spelling -> job Job Type spelling. */
export const JOB_TYPE_ACCOUNT_TO_JOB: Record<string, string> = {
  "new build - residential": "Residential - New Build",
  "more information needed": "",
  insurance: "",
};

/** Job Job Type spelling -> account Job Type spelling (T38); the modal's pick lands on both. */
export const JOB_TYPE_JOB_TO_ACCOUNT: Record<string, string> = {
  "residential - new build": "New Build - Residential",
};

/* ------------------------------------------------------------------------------------------
 * Lead source: one vocabulary (T40)
 *
 * A lead's source used to be spelled three ways — the picker's keys, the website ingest's
 * keys, and whatever JobTread held — and 14 spellings reached production. There is one list
 * now and it is JobTread's: every key below names a Lead Source option on the JobTread
 * customer account, so nothing is platform-only and nothing is dropped on push. The five
 * options JobTread lacked (Phone Call, Job Site / Truck, Repeat Customer, Walk Your Plans,
 * Other) are being added to that option list by hand; until a live read carries them,
 * `jtOption` omits the field rather than failing the whole mutation, and the engine's
 * `optionDrift` names each option it could not find on the 30-minute refresh.
 *
 * `canonicalLeadSource` folds every legacy spelling onto a key and leaves a value it has
 * never seen ALONE. Flattening the unrecognised to "other" would destroy the only evidence
 * that a new channel exists; migration 0040 rewrites on exactly this map and the same rule.
 * ---------------------------------------------------------------------------------------- */

export type LeadSource =
  | "google" | "internet_search" | "website" | "referral" | "facebook" | "instagram"
  | "next_door" | "angi" | "home_advisor" | "insurance" | "trade_show" | "phone"
  | "jobsite_truck" | "repeat_customer" | "walk_your_plans" | "other";

/** The vocabulary: the key the platform stores, the words a person reads, JobTread's option. */
export const LEAD_SOURCES: ReadonlyArray<{ key: LeadSource; label: string; jobtread: string }> = [
  { key: "google", label: "Google", jobtread: "Google" },
  { key: "internet_search", label: "Internet Search", jobtread: "Internet Search" },
  { key: "website", label: "Website", jobtread: "Website" },
  { key: "referral", label: "Referral", jobtread: "Referral" },
  { key: "facebook", label: "Facebook", jobtread: "Facebook" },
  { key: "instagram", label: "Instagram", jobtread: "Instagram" },
  { key: "next_door", label: "Next Door", jobtread: "Next Door" },
  { key: "angi", label: "Angi", jobtread: "Angi" },
  { key: "home_advisor", label: "Home Advisor", jobtread: "Home Advisor" },
  { key: "insurance", label: "Insurance", jobtread: "Insurance" },
  { key: "trade_show", label: "Trade Show", jobtread: "Trade Show" },
  { key: "phone", label: "Phone Call", jobtread: "Phone Call" },
  { key: "jobsite_truck", label: "Job Site / Truck", jobtread: "Job Site / Truck" },
  { key: "repeat_customer", label: "Repeat Customer", jobtread: "Repeat Customer" },
  { key: "walk_your_plans", label: "Walk Your Plans", jobtread: "Walk Your Plans" },
  { key: "other", label: "Other", jobtread: "Other" },
];

export const LEAD_SOURCE_KEYS: readonly LeadSource[] = LEAD_SOURCES.map((s) => s.key);

const LEAD_SOURCE_BY_KEY = new Map(LEAD_SOURCES.map((s) => [s.key as string, s]));

/**
 * Every other spelling that has ever been written, keyed in the normalised form below, so
 * "Next Door", "next door" and "next_door" are one lookup. Paid versus organic is attribution
 * and lives on `deals.attribution`, which is why google_ads, lsa and google_search are one key.
 */
const LEAD_SOURCE_ALIASES: Record<string, LeadSource> = {
  google_search: "google",
  google_ads: "google",
  google_ppc: "google",
  lsa: "google",
  adwords: "google",
  internet: "internet_search",
  web: "website",
  website_form: "website",
  web_form: "website",
  webform: "website",
  website_inquiry: "website",
  referral_customer: "referral",
  customer_referral: "referral",
  referral_friend: "referral",
  word_of_mouth: "referral",
  social: "facebook",
  social_media: "facebook",
  meta: "facebook",
  ig: "instagram",
  nextdoor: "next_door",
  angies_list: "angi",
  "angie's_list": "angi",
  homeadvisor: "home_advisor",
  tradeshow: "trade_show",
  home_show: "trade_show",
  phone_call: "phone",
  call: "phone",
  inbound_call: "phone",
  job_site_truck: "jobsite_truck",
  jobsite: "jobsite_truck",
  truck: "jobsite_truck",
  repeat: "repeat_customer",
  returning_customer: "repeat_customer",
  past_customer: "repeat_customer",
  walkyourplans: "walk_your_plans",
  wyp: "walk_your_plans",
};

/** lower, trim, and one underscore for any run of space, slash or hyphen. */
const normLeadSource = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[\s/\-]+/g, "_").replace(/^_+|_+$/g, "");

/**
 * The key a stored value means. A spelling the map knows becomes its key; a spelling it does
 * not know is returned as it was written (trimmed), never folded into "other"; null stays null.
 */
export function canonicalLeadSource(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = normLeadSource(trimmed);
  if (LEAD_SOURCE_BY_KEY.has(n)) return n;
  return LEAD_SOURCE_ALIASES[n] ?? trimmed;
}

/** The words a person reads for a stored value. An unknown spelling shows itself. */
export function leadSourceLabel(v: string | null | undefined): string | null {
  const key = canonicalLeadSource(v);
  if (key === null) return null;
  return LEAD_SOURCE_BY_KEY.get(key)?.label ?? key;
}

/**
 * CRM lead_source -> JobTread Lead Source option, derived from the table above so there is
 * one list and not two. Legacy spellings stay in it: the previous release is a rollback
 * target and still writes them, and 0040 is data-only, so a row can still carry one.
 * `jtOption` drops a mapped option JobTread does not have yet rather than guessing.
 */
export const LEAD_SOURCE_TO_JT: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  const add = (spelling: string, key: LeadSource) => {
    const jt = LEAD_SOURCE_BY_KEY.get(key)?.jobtread;
    if (!jt) return;
    out[spelling] = jt;
    // jtOption lowercases but does not normalise separators, so the spaced form goes in too.
    const spaced = spelling.replace(/_/g, " ");
    if (spaced !== spelling) out[spaced] = jt;
  };
  for (const s of LEAD_SOURCES) add(s.key, s.key);
  for (const [alias, key] of Object.entries(LEAD_SOURCE_ALIASES)) add(alias, key);
  return out;
})();

/** CRM lost_reason -> JobTread Lead Lost Reason option. */
export const LOST_REASON_TO_JT: Record<string, string> = {
  price_too_high: "Price",
  budget_financing: "Price",
  chose_competitor: "Competition",
  timing_postponed: "Timing",
  out_of_area: "Not a good fit",
  not_qualified: "Not a good fit",
  we_declined: "Not a good fit",
  scope_changed: "Customer changed mind",
  diy: "Customer changed mind",
  ghosted: "Unknown",
};

/**
 * Coerce a value for an option-typed custom field: pass it through when it is already a valid
 * option (case insensitive), otherwise map it, otherwise return undefined so the caller omits
 * the field entirely.
 */
export function jtOption(list: readonly string[], value: unknown, map?: Record<string, string>): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const raw = String(value).trim();
  const direct = list.find((o) => o.toLowerCase() === raw.toLowerCase());
  if (direct) return direct;
  const mapped = map?.[raw.toLowerCase()];
  return mapped && list.includes(mapped) ? mapped : undefined;
}

// ---------------------------------------------------------------------------
// The phone line's own doorman (T36)
// ---------------------------------------------------------------------------

/**
 * Why an inbound call or text on the main line never became a board card. The shape mirrors the
 * website form's `quarantine` exactly (`apps/site-functions/lead/index.mjs`, 2026-08-25): nothing
 * is ever dropped, the message is always recorded, and the deal is created **pre-disqualified**
 * rather than not created — so the Customers filter hides it, Triage lists it, and Restore is one
 * click. The website's four classes have kept spam off the board since August; the phone line had
 * no equivalent, and on 2026-09-09 it was minting thirty-one junk leads out of every fifty.
 */
export const PHONE_QUARANTINE_REASONS = {
  automated: "An automated code or notification",
  pitch: "Someone selling to us",
  toll_free: "A toll-free origin",
  no_words: "A call carrying no voicemail and no text",
  /** T41: the caller hung up in the call tree without pressing 1 or leaving a voicemail. */
  menu: "A call that ended in the menu",
  blocked: "The number is blocked",
} as const;
export type PhoneQuarantineReason = keyof typeof PHONE_QUARANTINE_REASONS;

/** The disqualify reason each quarantine class lands under. `no_words` is not a judgment about
 *  the person, only about what arrived, so it stays soft and blocks nobody. `menu` never creates
 *  a deal at all (the ingest records it on a known timeline and otherwise mints nothing); the
 *  mapping exists so the class is complete, and it is soft for the same reason. */
export const PHONE_QUARANTINE_DISQUALIFIES: Record<PhoneQuarantineReason, DisqualifiedReason> = {
  automated: "automated",
  pitch: "not_a_lead",
  toll_free: "spam",
  no_words: "unresponsive",
  menu: "unresponsive",
  blocked: "spam",
};

/**
 * Toll-free NANP prefixes. A customer in Wichita does not ring a builder from an 800 number;
 * every one of the four seen on 2026-09-09 was a recorded call.
 */
const TOLL_FREE_NPA = new Set(["800", "833", "844", "855", "866", "877", "888"]);

export function isTollFreeNumber(e164: string | null | undefined): boolean {
  const digits = (e164 ?? "").replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return national.length === 10 && TOLL_FREE_NPA.has(national.slice(0, 3));
}

/**
 * One-time passcodes and service notifications. Every phrase here was taken from a real message
 * on the JAKS line: Apple, Intuit and Link all send codes to the business number because it is
 * set as a recovery number somewhere. James chose to filter them at the door (2026-09-09) rather
 * than move them off the number.
 */
const AUTOMATED_RE = [
  /\b(?:verification|security|authentication|login|access)\s+code\b/i,
  /\byour\b[^.]{0,24}\bcode\s+is\b/i,
  /\bis\s+your\b[^.]{0,24}\bcode\b/i,
  /\bone[-\s]?time\s+(?:pass)?code\b/i,
  /\bnever\s+share\b[^.]{0,40}\bcode\b/i,
  /\bdon'?t\s+share\b[^.]{0,40}\b(?:code|anyone)\b/i,
  /\b2fa\b|\botp\b/i,
  /\bdo\s+not\s+reply\s+to\s+this\s+message\b/i,
];

/**
 * Someone selling to us rather than buying from us. Lead brokers are the persistent ones — the
 * "$55 each, fully exclusive" template arrived twice in a fortnight from different numbers.
 */
const PITCH_RE = [
  /\b(?:exclusive|qualified)\s+(?:leads?|estimates?|requests?)\b/i,
  /\$\s?\d{1,4}\s+(?:each|per\s+lead|\/\s?lead)\b/i,
  /\bpay\s+only\s+for\b/i,
  /\b(?:add|send\s+you|get\s+you)\s+\d{1,2}\s*(?:-|to|–)\s*\d{1,2}\s+more\b/i,
  /\b(?:health|insurance|medicare|dental)\s+plans?\b/i,
  /\b(?:rank\s+higher|first\s+page\s+of\s+google|seo\s+services?)\b/i,
  /\bgrow\s+your\s+business\b/i,
  /\b(?:newsletter|mailing\s+list|unsubscribe)\b/i,
];

export interface InboundPhoneEvent {
  /** The other party's number, E.164. */
  e164: string;
  /** `call` or `sms`. */
  channel: "call" | "sms";
  /** Message text for a text; a voicemail transcript for a call, when one exists. */
  body?: string | null;
  /** Call length in seconds. Ignored for a text. */
  durationSec?: number | null;
  /** True when this exact number is on `crm.blocked_numbers`. */
  blocked?: boolean;
  /** Quo's call status: `completed`, `no-answer`, `missed`, ... Ignored for a text. */
  status?: string | null;
  /** When a person picked up, from the call payload. Absent on a call nobody answered. */
  answeredAt?: string | null;
  /** The voicemail object (or any truthy marker) when the caller left one. */
  voicemail?: unknown;
}

/**
 * A call with nothing on it: no voicemail, no transcript, no text, and over almost before it
 * started. James, 2026-09-09, asked directly whether this should be a lead: "No." Sixteen of the
 * fifty. It is recorded on the timeline either way; it simply does not open a deal until they
 * say something, at which point `revivesQuarantinedDeal` puts one back.
 */
const WORDLESS_CALL_MAX_SEC = 15;

/** Quo statuses for a call that rang the reps and nobody picked up. */
const RANG_THE_REPS = new Set(["no-answer", "missed"]);

/**
 * Pure, total, and the single rule the ingest and the backlog screen both read. `null` means the
 * message reaches the board exactly as it does today. Order matters: `blocked` is a standing human
 * decision and outranks every guess made about content.
 *
 * Calls, since the press-1 tree (T41, production 2026-09-11): a call opens or revives a lead only
 * when a person answered (`answeredAt`), or it rang the reps (`no-answer`), or it left a
 * voicemail with words. `completed` with no `answeredAt` and no voicemail is a caller who hung up
 * in the menu: class `menu`, which the ingest records on a known number's timeline and otherwise
 * never mints. A call a person answered that carried no words and was over inside fifteen
 * seconds keeps the `no_words` class it always had.
 */
export function phoneQuarantineOf(ev: InboundPhoneEvent): PhoneQuarantineReason | null {
  if (ev.blocked) return "blocked";
  const body = (ev.body ?? "").trim();
  if (body) {
    if (AUTOMATED_RE.some((re) => re.test(body))) return "automated";
    if (PITCH_RE.some((re) => re.test(body))) return "pitch";
  }
  if (isTollFreeNumber(ev.e164)) return "toll_free";
  if (ev.channel === "call" && !body) {
    const status = (ev.status ?? "").toLowerCase();
    if (status === "completed" && !ev.answeredAt && !ev.voicemail) return "menu";
    if (RANG_THE_REPS.has(status)) return null;
    if ((ev.durationSec ?? 0) <= WORDLESS_CALL_MAX_SEC) return "no_words";
  }
  return null;
}

/**
 * The other half of the door, and the part that can lose a customer if it is wrong.
 *
 * Once a number has been quarantined it has an account and a contact, so a later message matches
 * and no prospect is created — which means without this rule a real inquiry from a previously
 * quarantined number would land on an account timeline and never reach the board at all. True
 * here means: open a fresh `new_lead` deal and start the clock.
 *
 * A blocked number is exempt: blocking is a human decision and content does not overturn it.
 */
export function revivesQuarantinedDeal(ev: InboundPhoneEvent): boolean {
  if (ev.blocked) return false;
  if (!(ev.body ?? "").trim()) return false;
  return phoneQuarantineOf(ev) === null;
}

// ---------------------------------------------------------------------------
// Naming the vendor on held vendor mail (T40)
// ---------------------------------------------------------------------------

const LEGAL_SUFFIXES = new Set(["llc", "inc", "incorporated", "co", "corp", "corporation", "company", "ltd", "limited", "lp", "llp", "pllc", "pc"]);

/** A company name as words: lowercase, "&" read as "and", punctuation gone, "The" and trailing legal suffixes dropped. */
function vendorNameWords(name: string | null | undefined): string[] {
  const words = (name ?? "")
    .toLowerCase()
    .replace(/\bl\.l\.c\b\.?/g, "llc")
    .replace(/[&+]/g, " and ")
    .replace(/[\u2019']/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  if (words[0] === "the") words.shift();
  while (words.length > 1 && LEGAL_SUFFIXES.has(words[words.length - 1]!)) words.pop();
  return words;
}

/**
 * A company name reduced to what identifies it, with no spaces, so "MJB Heating & Cooling",
 * "MJB Heating and Cooling" and "MJB HEATING AND COOLING, LLC" are one key, and so are
 * "Builders First Source" and "Builders FirstSource". Empty when nothing is left.
 */
export function vendorNameKey(name: string | null | undefined): string {
  return vendorNameWords(name).join("");
}

export interface VendorNameCandidate {
  id: string;
  name: string;
}

export type VendorNameHow = "sender name" | "subject line" | "vendor name in the domain";

export interface VendorNameSuggestion {
  id: string;
  how: VendorNameHow;
}

/** True when the vendor's key is a run of whole words in the text: "Builders FirstSource Invoices" holds "buildersfirstsource". */
function namedInWords(words: readonly string[], key: string): boolean {
  for (let i = 0; i < words.length; i++) {
    let run = "";
    for (let j = i; j < words.length && run.length < key.length; j++) {
      run += words[j];
      if (run === key) return true;
    }
  }
  return false;
}

/**
 * Which vendor a held mail is from, read from what the mail already says (T40): the sender's display
 * name, the subject line, then the sender's own domain. QuickBooks, ServiceCore and services like
 * them send every company's invoices from one address, but they put the company in the display name
 * ("MJB Heating and Cooling"), and a person writing for a supplier often puts it in the subject
 * ("Builders FirstSource Invoices 1038561").
 *
 * Each source is tried in that order, and the first one that names any vendor decides: one vendor is
 * the suggestion, two or more is no suggestion, since a weaker source must not choose between vendors
 * a stronger one could not tell apart. A name must be a run of whole words, and at least five
 * letters to be found inside a subject, so "Sage" is never read out of "message". Our own accounts
 * ("JAKS Wellington") are never a vendor here. The caller passes a domain only when it belongs to the
 * sender: never a free mail provider's or a shared service's.
 *
 * The answer is a suggestion. Mail matched this way is held for a person, who confirms the vendor and
 * picks the job; it never files a bill on its own.
 */
export function suggestVendorFromMail(
  vendors: readonly VendorNameCandidate[],
  mail: { senderName?: string | null; subject?: string | null; domain?: string | null },
): VendorNameSuggestion | null {
  const keyed = vendors
    .map((v) => ({ id: v.id, key: vendorNameKey(v.name) }))
    .filter((v) => v.key.length >= 3 && !v.key.startsWith("jaks"));
  const decide = (how: VendorNameHow, hits: Array<{ id: string }>): VendorNameSuggestion | null | undefined => {
    const ids = [...new Set(hits.map((h) => h.id))];
    if (!ids.length) return undefined;
    return ids.length === 1 ? { id: ids[0]!, how } : null;
  };

  const senderWords = vendorNameWords(mail.senderName);
  const senderKey = senderWords.join("");
  if (senderKey) {
    const exact = keyed.filter((v) => v.key === senderKey);
    const byName = decide("sender name", exact.length ? exact : keyed.filter((v) => v.key.length >= 5 && namedInWords(senderWords, v.key)));
    if (byName !== undefined) return byName;
  }

  const subjectWords = vendorNameWords(mail.subject);
  if (subjectWords.length) {
    const bySubject = decide("subject line", keyed.filter((v) => v.key.length >= 5 && namedInWords(subjectWords, v.key)));
    if (bySubject !== undefined) return bySubject;
  }

  const labels = (mail.domain ?? "").toLowerCase().split(".").filter(Boolean);
  // The name the company registered: "sherwin" in sherwin.com, and in billing.sherwin.co.uk.
  const secondLevel = labels.length >= 3 && /^(co|com|org|net|gov|ac)$/.test(labels[labels.length - 2]!) ? 3 : 2;
  const label = labels.length >= 2 ? (labels[labels.length - secondLevel] ?? "").replace(/[^a-z0-9]/g, "") : "";
  if (label.length >= 4) {
    const byDomain = decide("vendor name in the domain", keyed.filter((v) => v.key === label || (label.length >= 6 && v.key.startsWith(label))));
    if (byDomain !== undefined) return byDomain;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Naming a vendor from a conversation (T36.1)
// ---------------------------------------------------------------------------

/**
 * Words that begin a sentence or stand alone and so tell you nothing about a company.
 * Deliberately short: the point is to cut obvious noise, not to parse English.
 */
const NOT_A_COMPANY = new Set([
  "i", "we", "he", "she", "they", "it", "the", "a", "an", "and", "but", "so", "then", "hi", "hey",
  "hello", "thanks", "thank", "please", "call", "called", "calling", "text", "texted", "left",
  "voicemail", "message", "just", "can", "could", "would", "will", "is", "was", "are", "were",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "jaks", "jan", "feb", "mar", "apr", "jun", "jul", "aug", "sep", "sept", "oct", "nov", "dec",
]);

/**
 * Capitalised runs of two or more words, which is what a company reads like in a note somebody
 * typed: "River City Painting", "Castle Door and Hardware", "Adams Architects". Single capitalised
 * words are skipped — too many of them are the start of a sentence or a person's first name.
 */
export function companyNamesIn(text: string | null | undefined): string[] {
  const body = (text ?? "").replace(/\s+/g, " ");
  if (!body) return [];
  const out: string[] = [];
  // A run of capitalised words, allowing lowercase joiners like "and" / "of" inside it.
  const re = /\b([A-Z][A-Za-z&'.-]+(?:\s+(?:and|of|the|&)\s+[A-Z][A-Za-z&'.-]+|\s+[A-Z][A-Za-z&'.-]+)+)/g;
  for (const m of body.matchAll(re)) {
    const phrase = m[1]!.trim().replace(/[.,;:]+$/, "");
    const words = phrase.split(/\s+/);
    if (words.length < 2) continue;
    if (words.every((w) => NOT_A_COMPANY.has(w.toLowerCase()))) continue;
    // "Cara Milne provided" — drop a trailing lowercase-ish verb that got swept in.
    if (!out.includes(phrase)) out.push(phrase);
  }
  return out;
}

/**
 * Words that make a name a company whoever else it sounds like. "Grace Construction" is a firm
 * even when somebody called Grace works here; "Cara Milne" is not.
 */
const COMPANY_WORDS = new Set([
  "construction", "painting", "paint", "roofing", "drywall", "plumbing", "electric", "electrical",
  "hvac", "heating", "cooling", "concrete", "masonry", "lumber", "supply", "supplies", "hardware",
  "architects", "architecture", "design", "designs", "builders", "building", "contracting",
  "contractors", "remodeling", "remodelling", "services", "solutions", "systems", "group",
  "associates", "partners", "industries", "enterprises", "landscaping", "flooring", "cabinets",
  "cabinetry", "windows", "doors", "glass", "insulation", "excavating", "septic", "well",
  "properties", "realty", "insurance", "bank", "rental", "rentals", "equipment", "supplyco",
]);

/** Loose comparison: case, punctuation and the usual company suffixes do not count. */
function nameKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b(inc|llc|l\.l\.c|co|company|corp|corporation|ltd|the)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** True when the two names share any word of substance. "Adams Architects" vs "Adam Smith" → true. */
function sharesAWord(a: string, b: string): boolean {
  const wa = new Set(nameKey(a).split(" ").filter((w) => w.length > 2));
  return nameKey(b).split(" ").some((w) => w.length > 2 && wa.has(w));
}

export interface VendorNameWarning {
  /** The company the conversation actually names. */
  mentioned: string;
  /** True when it also looks confusingly like the vendor that was picked. */
  confusable: boolean;
}

/**
 * Why this exists. On 2026-09-09 a call logged as "Adam from River City Painting called…" was
 * assigned to the vendor **Adams Architects**, which soft-deleted the lead and moved the
 * conversation onto the wrong company's record. Nobody had to be careless for that to happen: the
 * picker offers a list of names and the right one was not in it, because River City Painting is
 * not a vendor in the platform at all.
 *
 * So: if the conversation names a company and it is not the one being picked, say so before the
 * move rather than after. Returns the names worth showing, most confusable first; empty means the
 * conversation names nobody, or names the vendor being picked.
 */
export function vendorNameWarnings(
  body: string | null | undefined,
  vendorName: string,
  /**
   * Names that are people rather than companies — pass the staff list. Without it the warning on
   * the real 2026-09-09 call read `names "River City Painting", "Cara Milne"`, and naming the
   * person who logged the call as a company is exactly the noise that gets a warning ignored.
   */
  ignoreNames: readonly string[] = [],
): VendorNameWarning[] {
  if (!vendorName.trim()) return [];
  const picked = nameKey(vendorName);
  const ignore = new Set(ignoreNames.map(nameKey).filter(Boolean));
  return companyNamesIn(body)
    .filter((n) => {
      const k = nameKey(n);
      if (ignore.has(k)) return false;
      // A person whose surname has changed. James, 2026-09-09: Cara Milne married and the
      // directory now says Cara McClenny, so the August calls that name her no longer matched.
      // A two-word name sharing its first name with somebody on staff, and carrying no word that
      // makes it a company, is that person under another surname rather than a firm.
      const words = k.split(" ").filter(Boolean);
      if (words.length === 2 && !words.some((w) => COMPANY_WORDS.has(w))) {
        const first = words[0]!;
        if ([...ignore].some((staff) => staff.split(" ")[0] === first)) return false;
      }
      // Nothing to warn about when the conversation names the vendor being picked.
      return k !== picked && !k.includes(picked) && !picked.includes(k);
    })
    .map((n) => ({ mentioned: n, confusable: sharesAWord(n, vendorName) }))
    .sort((a, b) => Number(b.confusable) - Number(a.confusable));
}

/* ------------------------------------------------------------------------------------------
 * Addresses (T38)
 *
 * "Is this the same place?" for the two moments the platform could otherwise make a second
 * copy of a location: a lead moved onto a customer JobTread already has, and a create_location
 * retried after a lost response. JobTread does NOT refuse a duplicate location (197 live
 * locations on 2026-09-11, "Wichita" among them eighteen times, twice on one customer), so a
 * duplicate never errors; it just quietly exists. Conservative on purpose: a street alone is
 * never enough, because "123 Main St" is in most towns. Both ZIPs must be known and agree.
 * ---------------------------------------------------------------------------------------- */

const STREET_WORDS: Record<string, string> = {
  street: "st", avenue: "ave", av: "ave", drive: "dr", lane: "ln", road: "rd", court: "ct",
  circle: "cir", boulevard: "blvd", place: "pl", parkway: "pkwy", terrace: "ter", highway: "hwy",
  north: "n", south: "s", east: "e", west: "w", northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw",
};

/** Lowercased, punctuation gone, suffixes and directions abbreviated the USPS way. */
export function normalizeStreet(s: string | null | undefined): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/[.,#]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => STREET_WORDS[w] ?? w)
    .join(" ");
}

/** The five-digit ZIP in a string, or null. The last one wins: street numbers come first. */
export function zipOf(s: string | null | undefined): string | null {
  const all = [...(s ?? "").matchAll(/\b(\d{5})(?:-\d{4})?\b/g)];
  return all.length ? all[all.length - 1]![1]! : null;
}

export interface AddressLike {
  street1?: string | null;
  postalCode?: string | null;
  formattedAddress?: string | null;
}

export function sameAddress(a: AddressLike, b: AddressLike): boolean {
  const street = (x: AddressLike) => normalizeStreet(x.street1 || (x.formattedAddress ?? "").split(",")[0]);
  const zip = (x: AddressLike) => (x.postalCode ? zipOf(x.postalCode) : zipOf(x.formattedAddress));
  const sa = street(a);
  const sb = street(b);
  if (!sa || sa !== sb) return false;
  const za = zip(a);
  const zb = zip(b);
  return Boolean(za && zb && za === zb);
}

/* ------------------------------------------------------------------------------------------
 * The website door: one verdict, and the acknowledgment gate (T41 B and C)
 *
 * `websiteSpamVerdict` is the single spam rule set. The ingest reads it at mint (the deal is
 * created pre-disqualified and sends no notification) and the board reads it through the sales
 * module's `spamSignals`, which is now a wrapper. Nothing here disqualifies anything on its own,
 * nothing is ever dropped, and Triage keeps Restore.
 *
 * Every rule was written against the census of the website submissions production took between
 * 2026-08-21 and 2026-09-11: 25 spam, 8 real, one of the 25 through every gate that existed. Those
 * 33 messages are the fixtures in `website-spam.test.ts` and they are the contract. A rule that
 * stops catching them has stopped doing its job; a rule that starts catching one of the eight real
 * ones is worse than the spam it stops, because the eight are customers.
 * ---------------------------------------------------------------------------------------- */

export type SpamSignalCode =
  | "fictional_phone"
  | "no_recaptcha"
  | "low_recaptcha"
  | "marketing_bait"
  | "fake_name"
  | "foreign_phone"
  | "toll_free"
  | "url_in_message"
  | "non_latin"
  | "suspicious_email"
  | "template_text"
  | "repeat_offender"
  | "rapid_repeat"
  | "own_reach";

export interface SpamSignal {
  code: SpamSignalCode;
  detail: string;
}

export interface WebsiteSpamLead {
  name?: string | null;
  email?: string | null;
  /** E.164 or raw; digits are extracted. */
  phone?: string | null;
  message?: string | null;
  /** True when the lead came in through the website form. */
  fromWebsite?: boolean;
  /** The reCAPTCHA score the site recorded; undefined/null means no token was posted. */
  recaptchaScore?: number | null;
  /**
   * The site Lambda's own verdict, when it had one: "no-js form path", "origin ...", "no recaptcha
   * token", "newsletter template text" (`apps/site-functions/lead/index.mjs`). No rule below reads
   * it — the rules judge what arrived, not who judged it — but `acknowledgmentEligible` refuses
   * outright when it is set, because the door has already said no once.
   */
  quarantine?: string | null;
  /** "How did you hear about us?". Carried so a caller can pass its own lead object straight in;
   *  no rule reads it today. */
  heardFrom?: string | null;
  /** The form's "Text me about my project" opt-in. It chooses the acknowledgment channel. */
  consentSms?: boolean;
}

export interface WebsiteSpamContext {
  /** The resolver found this name or email on an account already disqualified as spam. */
  priorSpamMatch?: boolean;
  /** Submissions from this email or phone in the last twenty-four hours, this one included. */
  submissionsLast24h?: number;
  /**
   * T44: the form carried a JAKS phone line, a company address or an employee's reach. The lead is
   * still recorded, pre-disqualified and restorable, but never matched or claimed on that reach.
   */
  ownReaches?: readonly OwnReach[];
}

const BAIT_PHRASES = [
  "send me news and updates",
  "product news",
  "news and updates",
  "subscribe me",
  "sign me up for",
  "seo services",
  "rank your website",
  "backlinks",
  "guest post",
  "increase your traffic",
  "web design services",
  "we can build your website",
  "cheap leads",
];

const FAKE_NAME_WORDS = new Set([
  "test",
  "testing",
  "asdf",
  "qwerty",
  "admin",
  "user",
  "anonymous",
  "unknown",
  "none",
  "null",
  "sample",
  "example",
  "john doe",
  "jane doe",
  "lorem ipsum",
]);

/**
 * Templates, not sentences. Each of these is a phrase that appeared verbatim in the census and
 * that no customer of a Wichita builder has ever written.
 *
 * Two of them are deliberately paired rather than standalone. "Please contact me by email" on its
 * own is something a real person says; it is the directory keyword stuffed after it that makes it
 * a template. "$25,000" on its own is a kitchen budget; it is the prize language around it that
 * makes it a giveaway. Pairing is what keeps the eight real leads clean.
 */
const TEMPLATE_RULES: ReadonlyArray<{ label: string; test: (msg: string) => boolean }> = [
  { label: "promo code", test: (m) => /promo code/.test(m) },
  { label: "jackpot", test: (m) => /jackpot/.test(m) },
  { label: "playstation", test: (m) => /playstation/.test(m) },
  { label: "win a", test: (m) => /\bwin a\b/.test(m) },
  { label: "high roller", test: (m) => /high roller/.test(m) },
  { label: "$25,000 prize", test: (m) => /\$\s?25,?000/.test(m) && /promo|code|ticket|prize|win|gift|reward|giveaway/.test(m) },
  {
    label: "please contact me by email — general contractor",
    test: (m) => m.includes("please contact me by email") && /general contractor|construction company|home builder|remodeling contractor/.test(m),
  },
  {
    // The one submission in the census that passed every gate production had: a real US mobile, a
    // 0.9 reCAPTCHA score, a gmail address, and this sentence. The pitch is the only tell.
    label: "looking for a reliable construction company",
    test: (m) => /looking for a reliable|reliable (?:construction company|contractor|builder)/.test(m) && /construction|contractor|builder|remodel/.test(m),
  },
];

/** Free-mail domains that only ever carried spam here, plus the two registries they sit under. */
const SPAM_EMAIL_DOMAINS = new Set(["mail.ru", "list.ru", "bk.ru", "inbox.ru", "yandex.ru", "rambler.ru"]);

/**
 * A link in a first inquiry. Protocol and `www.` forms, plus a bare host with a path — the census
 * used `telegra.ph/` that way. A bare host with no path is NOT a link here: a real customer types
 * their own email address into the message often enough to matter.
 */
const URL_IN_MESSAGE_RE = /\b(?:https?:\/\/|www\.)\S+|\b[a-z0-9-]{2,}\.(?:com|net|org|ru|su|info|biz|xyz|top|online|site|link|shop|ph)\//i;

/** Below this a message is too short for the alphabet ratio to mean anything. */
const MIN_LETTERS_FOR_SCRIPT = 12;

/** A message with fewer than this share of its letters in the Latin alphabet is not for us. */
const MIN_LATIN_SHARE = 0.7;

function nanpNationalOf(raw: string): string | null {
  const e164 = toE164(raw);
  return e164 && e164.startsWith("+1") && e164.length === 12 ? e164.slice(2) : null;
}

/** The NANP's reserved fictional block: any area code, exchange 555, line 0100..0199. */
function isFictionalNanp(national: string): boolean {
  if (national.slice(3, 6) !== "555") return false;
  const line = Number(national.slice(6));
  return line >= 100 && line <= 199;
}

/**
 * Pure and total. Returns the signals found; an empty array means nothing suspicious.
 *
 * `ctx` carries the two facts a pure function cannot know — whether this name or email is already
 * on an account disqualified as spam, and how many times this email or phone has submitted today.
 * Both are optional: the board calls this with the row it is already holding and passes neither.
 */
export function websiteSpamVerdict(lead: WebsiteSpamLead, ctx?: WebsiteSpamContext): SpamSignal[] {
  const out: SpamSignal[] = [];

  const rawPhone = (lead.phone ?? "").trim();
  if (rawPhone.replace(/\D/g, "")) {
    const national = nanpNationalOf(rawPhone);
    if (!national) {
      out.push({ code: "foreign_phone", detail: `"${rawPhone}" is not a North American number` });
    } else {
      if (isFictionalNanp(national)) {
        out.push({
          code: "fictional_phone",
          detail: `${national.slice(0, 3)}-555-${national.slice(6)} is a reserved fictional number`,
        });
      }
      if (isTollFreeNumber(`+1${national}`)) {
        out.push({ code: "toll_free", detail: `${national.slice(0, 3)} is a toll-free area code, and a customer does not ring a builder from one` });
      }
    }
  }

  if (lead.fromWebsite) {
    if (lead.recaptchaScore === undefined || lead.recaptchaScore === null) {
      out.push({
        code: "no_recaptcha",
        detail: "Website post carried no reCAPTCHA token (the form fails open, so real browsers always send one)",
      });
    } else if (lead.recaptchaScore < 0.3) {
      out.push({ code: "low_recaptcha", detail: `reCAPTCHA score ${lead.recaptchaScore} is below the 0.3 bot threshold` });
    }
  }

  const message = (lead.message ?? "").trim();
  const msg = message.toLowerCase();

  const bait = BAIT_PHRASES.find((p) => msg.includes(p));
  if (bait) out.push({ code: "marketing_bait", detail: `Message reads like a newsletter or vendor pitch ("${bait}")` });

  const template = TEMPLATE_RULES.find((r) => r.test(msg));
  if (template) out.push({ code: "template_text", detail: `Message is a known spam template ("${template.label}")` });

  if (URL_IN_MESSAGE_RE.test(message)) {
    out.push({ code: "url_in_message", detail: "Message carries a link, which a first inquiry from a customer does not" });
  }

  const letters = message.match(/\p{L}/gu)?.length ?? 0;
  const latin = message.match(/[A-Za-z]/g)?.length ?? 0;
  if (letters >= MIN_LETTERS_FOR_SCRIPT && latin / letters < MIN_LATIN_SHARE) {
    out.push({
      code: "non_latin",
      detail: `Only ${Math.round((latin / letters) * 100)} percent of the message is written in the Latin alphabet`,
    });
  }

  const rawEmail = (lead.email ?? "").trim();
  const at = rawEmail.lastIndexOf("@");
  if (at > 0 && at < rawEmail.length - 1) {
    const local = rawEmail.slice(0, at);
    const domain = rawEmail.slice(at + 1).toLowerCase();
    const host = domain.split(".")[0] ?? "";
    const why = SPAM_EMAIL_DOMAINS.has(domain)
      ? `${domain} is on the block list`
      : /\.(?:ru|su)$/.test(domain)
        ? `${domain} is registered under .${domain.split(".").pop()}`
        : /[A-Z]/.test(local) && !/[a-z]/.test(local)
          ? `"${local}" is written entirely in capitals`
          : host.length >= 4 && /^[a-z]+$/.test(host) && !/[aeiouy]/.test(host)
            ? `"${domain}" has no vowel in it`
            : null;
    if (why) out.push({ code: "suspicious_email", detail: `Email address is suspect: ${why}` });
  }

  const name = (lead.name ?? "").trim().toLowerCase();
  if (name) {
    const words = name.split(/\s+/);
    const fake =
      FAKE_NAME_WORDS.has(name) ||
      words.every((w) => FAKE_NAME_WORDS.has(w)) ||
      /^(.)\1{2,}$/.test(name.replace(/\s/g, "")) ||
      /\d{3,}/.test(name) ||
      /^[^a-z]*$/.test(name) ||
      (/^[a-z]{8,}$/.test(name) && !/[aeiouy]/.test(name));
    if (fake) out.push({ code: "fake_name", detail: `"${lead.name}" does not look like a real name` });
  }

  if (ctx?.priorSpamMatch) {
    out.push({ code: "repeat_offender", detail: "This name or email is already on an account disqualified as spam" });
  }
  const submissions = ctx?.submissionsLast24h ?? 0;
  if (submissions > 2) {
    out.push({ code: "rapid_repeat", detail: `${submissions} submissions from this email or phone in twenty-four hours` });
  }

  for (const reach of ctx?.ownReaches ?? []) {
    out.push({ code: "own_reach", detail: `${reach.kind === "phone" ? displayPhone(reach.value) : reach.value} is ${reach.owner}, not a customer's` });
  }

  return out;
}

/** The send bar, higher than the 0.3 bar the site stores at: a text goes out or it does not. */
export const ACKNOWLEDGMENT_MIN_SCORE = 0.7;

/** James, 2026-09-11: ten a day, and a log line an alarm can watch. */
export const ACKNOWLEDGMENT_DAILY_CAP = 10;

export interface AcknowledgmentContext {
  /** The submission came through the site's JavaScript path, not a form post at the endpoint. */
  jsPath: boolean;
  /** The Origin header was the site. */
  originOk: boolean;
  /** The same score the site recorded, at the send bar rather than the store bar. */
  recaptchaScore: number | null;
  /** This number is on `crm.blocked_numbers`. */
  blocked: boolean;
  /** The resolved account says do not contact. */
  doNotContact: boolean;
  /** The resolver attached this to an account or deal already disqualified as spam. */
  priorSpamMatch: boolean;
  /** This account has already been acknowledged in the last twenty-four hours. */
  acknowledgedWithin24h: boolean;
  /** Acknowledgments already sent today. */
  sentToday: number;
  /** Defaults to `ACKNOWLEDGMENT_DAILY_CAP`. */
  dailyCap?: number;
  /**
   * Which door the lead came through (T53). Website is the default. A Meta lead (a Facebook or
   * Instagram Instant Form) has no JavaScript path, no Origin header and no reCAPTCHA score, so
   * those three clauses cannot apply to it; it has two proofs of its own instead.
   */
  channel?: "website" | "meta";
  /** The Meta channel's proofs: the delivery verified against the app secret, the lead read from Meta by id. */
  metaProofs?: { signatureVerified: boolean; fetchedById: boolean };
}

export type AcknowledgmentVerdict = { ok: true; channel: "sms" | "email" } | { ok: false; reason: string };

/**
 * Whether the fixed-template website acknowledgment may go out, and down which channel.
 *
 * Rule 12's standing exception covers this message and only this message: it is a fixed template
 * approved once, it names no offer, and it carries STOP. Everything else about it is refusal —
 * every clause below was a way a scammer could be texted. `reason` is short on purpose: it is
 * logged per refusal and read in a list.
 */
export function acknowledgmentEligible(lead: WebsiteSpamLead, ctx: AcknowledgmentContext): AcknowledgmentVerdict {
  if (ctx.channel === "meta") {
    // T53. The receiver verified the signature and read the lead from Meta before anything was
    // written; both are stated here rather than assumed, so a caller that skipped either is refused.
    if (!ctx.metaProofs?.signatureVerified) return { ok: false, reason: "delivery not verified" };
    if (!ctx.metaProofs.fetchedById) return { ok: false, reason: "lead not read from Meta" };
    if (lead.quarantine) return { ok: false, reason: "quarantined at the door" };
  } else {
    if (!ctx.jsPath) return { ok: false, reason: "no-js form path" };
    if (!ctx.originOk) return { ok: false, reason: "origin not the site" };
    if (lead.quarantine) return { ok: false, reason: "quarantined at the door" };

    const score = ctx.recaptchaScore;
    if (score === null || score === undefined) return { ok: false, reason: "no reCAPTCHA score" };
    if (score < ACKNOWLEDGMENT_MIN_SCORE) return { ok: false, reason: `score ${score} below ${ACKNOWLEDGMENT_MIN_SCORE}` };
  }

  const rawPhone = (lead.phone ?? "").trim();
  if (!rawPhone) return { ok: false, reason: "no phone number" };
  const national = nanpNationalOf(rawPhone);
  if (!national) return { ok: false, reason: "foreign number" };
  if (isFictionalNanp(national)) return { ok: false, reason: "fictional number" };
  if (isTollFreeNumber(`+1${national}`)) return { ok: false, reason: "toll-free number" };
  if (ctx.blocked) return { ok: false, reason: "blocked number" };

  const signals = websiteSpamVerdict(lead);
  if (signals.length > 0) return { ok: false, reason: `spam signal ${signals[0]!.code}` };

  if (ctx.doNotContact) return { ok: false, reason: "do not contact" };
  if (ctx.priorSpamMatch) return { ok: false, reason: "prior spam match" };
  if (ctx.acknowledgedWithin24h) return { ok: false, reason: "acknowledged within twenty-four hours" };

  const cap = ctx.dailyCap ?? ACKNOWLEDGMENT_DAILY_CAP;
  if (ctx.sentToday >= cap) return { ok: false, reason: `daily cap of ${cap} reached` };

  return { ok: true, channel: lead.consentSms === true ? "sms" : "email" };
}

/* ----------------------------------------------------------------------------------------
 * Qualification (T43). Two closed lists a person picks from on the record; the other three
 * qualification fields are free text or a yes/no. Stored as the key, read as the label, and
 * never sent to JobTread, which has no field for either.
 * ---------------------------------------------------------------------------------------- */

export const BUDGET_RANGES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "under_25k", label: "Under $25k" },
  { key: "25k_50k", label: "$25k to $50k" },
  { key: "50k_100k", label: "$50k to $100k" },
  { key: "100k_250k", label: "$100k to $250k" },
  { key: "over_250k", label: "Over $250k" },
  { key: "undecided", label: "Not decided" },
];

export const PROJECT_TIMELINES: ReadonlyArray<{ key: string; label: string }> = [
  { key: "asap", label: "As soon as possible" },
  { key: "1_3_months", label: "1 to 3 months" },
  { key: "3_6_months", label: "3 to 6 months" },
  { key: "6_12_months", label: "6 to 12 months" },
  { key: "over_12_months", label: "More than a year" },
  { key: "undecided", label: "Not decided" },
];

/* ----------------------------------------------------------------------------------------
 * A text sent from the Quo app, as the timeline records it (T41).
 *
 * The platform learns about a text a rep typed in the Quo app only from the delivery receipt
 * (`message.delivered`, or `message.failed`), which carries the whole message. This is the one
 * mapping from that receipt to a `crm.activities` row, read by the ingest as receipts arrive and
 * by `scripts/backfill-outbound-texts.ts` over the receipts stored before the ingest recorded
 * them, so the two can never disagree about what a sent text looks like. Pure: the party the
 * text went to is resolved by the caller from `counterparty`.
 * ---------------------------------------------------------------------------------------- */

export interface QuoReceiptEnvelope {
  id?: string;
  type?: string;
  createdAt?: string;
}

export interface OutboundTextActivity {
  quoMessageId: string;
  kind: "sms";
  direction: "out";
  externalSource: "quo";
  externalId: string;
  dedupeKey: string;
  subject: string;
  body: string;
  /** ISO-8601, the message's own time. */
  occurredAt: string;
  deliveryStatus: "delivered" | "sent" | "failed";
  failureReason: string | null;
  /** E.164 of the person texted, or null when the receipt names nobody but us. */
  counterparty: string | null;
  payload: Record<string, unknown>;
}

function quoTextStatusOf(status: unknown, receiptType: string): OutboundTextActivity["deliveryStatus"] {
  const s = String(status ?? "").toLowerCase();
  if (s === "delivered") return "delivered";
  if (s === "sent" || s === "queued" || s === "sending") return "sent";
  if (s === "undelivered" || s === "failed" || s === "error") return "failed";
  return receiptType === "message.delivered" ? "delivered" : receiptType === "message.failed" ? "failed" : "sent";
}

/** The carrier's reason, from whichever field OpenPhone put it in. */
function quoFailureReasonOf(obj: Record<string, unknown>): string | null {
  for (const c of [obj.error, obj.errorMessage, obj.errorCode, obj.failureReason, obj.statusReason, obj.reason, obj.description]) {
    if (typeof c === "string" && c.trim()) return c.trim().slice(0, 500);
    if (c && typeof c === "object") {
      const m = (c as { message?: unknown; code?: unknown }).message ?? (c as { code?: unknown }).code;
      if (typeof m === "string" && m.trim()) return m.trim().slice(0, 500);
    }
  }
  return null;
}

function quoNumberList(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

/**
 * The activity fields for one delivered (or failed) outgoing text, or null when the receipt is
 * not about an outgoing message at all (no id, or `direction` says incoming). `ourNumbers` are
 * the JAKS lines, so a receipt that lists both ends still names the customer; without them the
 * first `to` is the customer, which is what an outgoing message means. A receipt whose only
 * recipients are JAKS lines names nobody. `now` is only the
 * fallback for a receipt with no timestamp anywhere on it.
 */
export function outboundTextFromQuoEvent(
  obj: Record<string, unknown>,
  envelope: QuoReceiptEnvelope,
  ourNumbers: readonly string[] = [],
  now: Date = new Date(),
): OutboundTextActivity | null {
  const quoMessageId = String(obj.id ?? envelope.id ?? "").trim();
  if (!quoMessageId) return null;
  if (String(obj.direction ?? "").toLowerCase() === "incoming") return null;

  const ours = new Set(ourNumbers.map((n) => toE164(n) ?? n).filter(Boolean));
  const to = quoNumberList(obj.to).map((t) => toE164(t)).filter((t): t is string => !!t);
  // An outgoing message is to its recipients, never from them: `from` is JAKS whether or not our
  // numbers are configured, so falling back to it named the main line as the customer (T44).
  const counterparty = to.find((t) => !ours.has(t)) ?? null;

  const rawBody = obj.body ?? obj.text ?? obj.content ?? "";
  const body = typeof rawBody === "string" ? rawBody : "";

  let occurredAt: string | null = null;
  for (const c of [obj.createdAt, obj.completedAt, envelope.createdAt]) {
    if (typeof c === "string") {
      const t = Date.parse(c);
      if (Number.isFinite(t)) {
        occurredAt = new Date(t).toISOString();
        break;
      }
    }
  }

  const receiptType = String(envelope.type ?? "");
  const deliveryStatus = quoTextStatusOf(obj.status, receiptType);
  const failureReason = deliveryStatus === "failed" ? quoFailureReasonOf(obj) : null;
  const subject = deliveryStatus === "failed" ? `Text not delivered: ${failureReason ?? "the carrier gave no reason"}` : "Text sent";

  return {
    quoMessageId,
    kind: "sms",
    direction: "out",
    externalSource: "quo",
    externalId: quoMessageId,
    dedupeKey: `quo:${quoMessageId}`,
    subject,
    body,
    occurredAt: occurredAt ?? now.toISOString(),
    deliveryStatus,
    failureReason,
    counterparty,
    payload: {
      quo_message_id: quoMessageId,
      quo_event_id: envelope.id ?? null,
      conversation_id: obj.conversationId ?? null,
      phone_number_id: obj.phoneNumberId ?? null,
      user_id: obj.userId ?? null,
      from: obj.from ?? null,
      to: quoNumberList(obj.to),
      media: Array.isArray(obj.media) ? obj.media : [],
      counterparty,
      delivery_status: deliveryStatus,
      delivery_error: failureReason,
      receipt_type: receiptType || null,
      sent_from: "quo_app",
    },
  };
}
